import React, { useEffect, useMemo, useRef, useState, Fragment } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Box,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  Chip,
  InputAdornment,
  Skeleton,
  CircularProgress,
  Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MedicationIcon from "@mui/icons-material/Medication";
import BadgeIcon from "@mui/icons-material/Badge";
import CreateIcon from "@mui/icons-material/Create";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlagIcon from "@mui/icons-material/Flag";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import PrintIcon from "@mui/icons-material/Print";
import PaymentSummary from "../components/PaymentSummary";
import CashPaymentDialog from "./CashPaymentDialog";
import IDCaptureModal from "./IDCaptureModal";
import SignatureCaptureModal from "./SignatureCaptureModal";
import ConsultationNotesDialog from "./ConsultationNotesDialog";
import ScanIdDialog from "./ScanIdDialog";
import CancelDialog from "./CancelDialog";
import IncompleteOrdersDialog from "./IncompleteOrdersDialog";
import PatientOrdersCollectPanel from "../components/PatientOrdersCollectPanel";
import EpicFlagsDialog from "./EpicFlagsDialog";
import { formatCurrency, getOrderPaymentBreakdown } from "../helpers/orderUtils";
import { toast } from "react-toastify";
import {
  fetchSignatureRequirements,
  buildPrescriptionsFromOrders,
  submitSignatureFlagsForRequirements,
  fetchAccountSignatureFlagCodes,
} from "../services/SignatureFlagsService";
import { getFlagObjectsFromApiCodes } from "../../../shared/dummyPrescriptionFlags";
import SearchUtils from "../utils/SearchUtils";
import LOCATION_TYPE from "../../../enum/location_type";

const getDisplayCode = (...values) => {
  const value = values.find((candidate) => {
    if (candidate === null || candidate === undefined) return false;
    const normalized = String(candidate).trim().toLowerCase();
    return normalized !== "" && normalized !== "null" && normalized !== "undefined";
  });

  return value === undefined ? "" : String(value).trim();
};

/** Shared-shelf products verify handoff by stock code; Counter bags use location code. */
const usesStockCodeVerify = (locationTypeId) =>
  locationTypeId === LOCATION_TYPE.Bedside ||
  locationTypeId === LOCATION_TYPE.Curbside;


const MultiPickupDialog = ({
  open,
  hideOnPrint = false,
  onClose,
  t,
  patientOrders = [],
  customerName = "",
  customerDob = "",
  customerPhone = "",
  customerEmail = "",
  popupData = null,
  consultationNotes = "",
  DEVICE_TYPE,
  orderBagScanStatus = {},
  onUpdateBagScanStatus = null,
  onTogglePaymentStatus,
  isUpdatingPaymentStatus = false,
  paymentStatusError = "",
  onTerminalPayment,
  isProcessingTerminalPayment = false,
  onPayrollDeduct,
  isProcessingPayLater = false,
  onCashPayment,
  isProcessingCashPayment = false,
  onCaptureSignature,
  onSubmitIdCapture,
  onSaveConsultation,
  isSavingConsultation = false,
  onDeclineConsultation,
  isDecliningConsultation = false,
  consultationError = "",
  onStartConsult,
  isStartingConsultation = false,
  onScanId,
  isScanningId = false,
  scanIdError = "",
  onBatchComplete,
  onCancelOrder,
  onReminderOrder,
  onReprintOrder,
  onReturnOrder,
  prescriptionHoldResume = false,
  onHoldOrder,
  onReleaseOrder,
  onUpdateOrderDueAmount,
  orderCompletionStatus = {},
  orderFullDetailsMap = {},
  selectedOrderId: selectedOrderIdProp = null,
  loading = false,
  accountDetails = null,
  enableTrim = false,
  trimChars = SearchUtils.defaultTrimChars,
  payLaterMode = "disabled",
  paymentDeviceSelector = null,
}) => {
  // State management
  const [bagScanValues, setBagScanValues] = useState({});
  const [bagScanErrors, setBagScanErrors] = useState({});
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);
  
  // Modal states
  const [isIdCaptureModalOpen, setIdCaptureModalOpen] = useState(false);
  const [isSignatureModalOpen, setSignatureModalOpen] = useState(false);
  const [isConsultationNotesDialogOpen, setConsultationNotesDialogOpen] = useState(false);
  const [isScanIdDialogOpen, setScanIdDialogOpen] = useState(false);
  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isConfirmRecaptureOpen, setConfirmRecaptureOpen] = useState(false);
  const [isConfirmRescanOpen, setConfirmRescanOpen] = useState(false);
  const [isConfirmRecaptureNotesOpen, setConfirmRecaptureNotesOpen] = useState(false);
  const [isIndividualRecaptureConfirmOpen, setIndividualRecaptureConfirmOpen] = useState(false);
  const [selectedOrderIdForRecapture, setSelectedOrderIdForRecapture] = useState(null);
  const [selectedNoteForRecapture, setSelectedNoteForRecapture] = useState(null);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [remindingOrderId, setRemindingOrderId] = useState(null);
  const [pendingBatchCompleteIsDispense, setPendingBatchCompleteIsDispense] = useState(false);
  const [isSubmittingSignature, setSubmittingSignature] = useState(false);
  const [isIncompleteOrdersDialogOpen, setIsIncompleteOrdersDialogOpen] = useState(false);
  const [incompleteOrdersList, setIncompleteOrdersList] = useState([]);
  const [isCashPaymentDialogOpen, setCashPaymentDialogOpen] = useState(false);
  
  // Epic flags dialog state
  const [epicFlagsDialogOpen, setEpicFlagsDialogOpen] = useState(false);
  const [selectedItemEpicFlags, setSelectedItemEpicFlags] = useState(null);

  const [editedItemPrices, setEditedItemPrices] = useState({});
  const [editingPriceKey, setEditingPriceKey] = useState(null);
  const [addonConfirmingId, setAddonConfirmingId] = useState("");
  const [addonConfirmedOrders, setAddonConfirmedOrders] = useState({});

  const confirmAddedItems = async (orderId) => {
    if (!orderId || addonConfirmingId) return;
    setAddonConfirmingId(orderId);
    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const token =
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken;
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}delivery/patient-marketplace/confirm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ order_id: orderId }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        toast.error(data.message || "Could not confirm the added items.");
        return;
      }
      setAddonConfirmedOrders((current) => ({ ...current, [orderId]: true }));
      toast.success("The added items are with the order.");
    } catch (error) {
      toast.error("Could not confirm the added items.");
    } finally {
      setAddonConfirmingId("");
    }
  };
  
  // Image refresh trigger to force re-render after capture
  const [imageRefreshKey, setImageRefreshKey] = useState(0);

  // Prescription-level signature requirements (flag forms to sign, append to PDF)
  const [signatureRequirements, setSignatureRequirements] = useState([]);
  const [signaturePickupId, setSignaturePickupId] = useState(null);
  const [signatureAccountId, setSignatureAccountId] = useState(null);
  const [signatureRequirementsLoading, setSignatureRequirementsLoading] = useState(false);
  // Account signature flag codes from GET /accounts/:id/signature-flags (only source; do not use signature_flags_test_config)
  const [accountSignatureFlagCodes, setAccountSignatureFlagCodes] = useState([]);
  // When user has submitted signature for requirements in this session: hide "Signature required" and "AOB On File" (state so UI re-renders)
  const [signatureRequirementsFulfilled, setSignatureRequirementsFulfilled] = useState(false);
  const [signatureSignedFlagLabels, setSignatureSignedFlagLabels] = useState([]);

  // Refs for scroll-to functionality
  const orderCardRefs = useRef({});
  const bagScanInputRefs = useRef({});
  const ordersListRef = useRef(null);
  const remindingRef = useRef(false);
  // After user submits signature for requirements, avoid re-showing them when a refetch runs (e.g. parent updates orderFullDetailsMap)
  const signatureRequirementsFulfilledRef = useRef(false);

  // Filter unprocessed orders
  const unprocessedOrders = useMemo(() => {
    return (patientOrders || []).filter((order) => {
      if (!order) return false;
      const typeId = order.type_id;
      return (
        typeId !== DEVICE_TYPE.Completed &&
        typeId !== DEVICE_TYPE.Returned &&
        typeId !== DEVICE_TYPE.Cancelled
      );
    });
  }, [patientOrders, DEVICE_TYPE]);

  // Determine if we should show skeleton loaders
  const showSkeleton = useMemo(() => {
    return loading || (unprocessedOrders.length === 0 && patientOrders.length === 0) || !popupData;
  }, [loading, unprocessedOrders.length, patientOrders.length, popupData]);


  // Get box details from first order
  const boxDetails = useMemo(() => {
    if (unprocessedOrders.length > 0) {
      const firstOrder = orderFullDetailsMap[unprocessedOrders[0].id] || unprocessedOrders[0];
      return firstOrder.boxDetails || {};
    }
    return {};
  }, [unprocessedOrders, orderFullDetailsMap]);

  // Account ID for API calls (same logic as signature-requirements useEffect)
  const accountIdForDialogs = useMemo(() => {
    if (!unprocessedOrders.length) return null;
    const firstOrder = unprocessedOrders[0];
    const details = orderFullDetailsMap[firstOrder.id] || firstOrder;
    return details.parent_account_id != null
      ? details.parent_account_id
      : (details.account && details.account.id) != null
        ? details.account.id
        : details.customer_id != null
          ? details.customer_id
          : firstOrder.customer_id != null
            ? firstOrder.customer_id
            : (accountDetails && accountDetails.id) != null
              ? accountDetails.id
              : null;
  }, [unprocessedOrders, orderFullDetailsMap, accountDetails]);

  const isPosEnabled = boxDetails.is_pos === true;
  const isConsultEnabled = boxDetails.is_consult === true;
  const isDlCaptureEnabled = boxDetails.is_dl_capture === true;

  const hasEpicFlags = (item) => {
    if (!item || !item.epic_flag_response) return false;
    const epicFlags = item.epic_flag_response;
    const hasFlags = epicFlags.flags && Array.isArray(epicFlags.flags) && epicFlags.flags.length > 0;
    const hasPrescriptionFlags = epicFlags.prescription_flags && Array.isArray(epicFlags.prescription_flags) && epicFlags.prescription_flags.length > 0;
    const hasCannotDispenseReasons = epicFlags.cannot_dispense_reasons && Array.isArray(epicFlags.cannot_dispense_reasons) && epicFlags.cannot_dispense_reasons.length > 0;
    return hasFlags || hasPrescriptionFlags || hasCannotDispenseReasons;
  };

  const [selectedOrderIdForEpicFlags, setSelectedOrderIdForEpicFlags] = useState(null);

  const handleOpenEpicFlagsDialog = (item, orderId) => {
    setSelectedItemEpicFlags(item);
    setSelectedOrderIdForEpicFlags(orderId != null ? orderId : null);
    setEpicFlagsDialogOpen(true);
  };

  const handleCloseEpicFlagsDialog = () => {
    setEpicFlagsDialogOpen(false);
    setSelectedItemEpicFlags(null);
    setSelectedOrderIdForEpicFlags(null);
  };

  // Get scanned and verified order IDs (defined early for use in other hooks)
  const scannedOrderIds = useMemo(() => {
    return unprocessedOrders
      .filter((order) => {
        const scanStatus = orderBagScanStatus[order.id];
        return scanStatus && scanStatus.isScanned === true && scanStatus.isVerified === true;
      })
      .map((order) => order.id);
  }, [unprocessedOrders, orderBagScanStatus]);

  // Fetch prescription-level signature requirements when dialog opens (iLocalBox / flag forms)
  // Source of flags: GET /accounts/:accountId/signature-flags only (do not use signature_flags_test_config)
  useEffect(() => {
    if (!open || !unprocessedOrders.length) {
      setSignatureRequirements([]);
      setSignaturePickupId(null);
      setSignatureAccountId(null);
      setAccountSignatureFlagCodes([]);
      setSignatureRequirementsFulfilled(false);
      setSignatureSignedFlagLabels([]);
      signatureRequirementsFulfilledRef.current = false;
      return;
    }
    const firstOrder = unprocessedOrders[0];
    const details = orderFullDetailsMap[firstOrder.id] || firstOrder;
    const accountIdVal = details.parent_account_id != null
      ? details.parent_account_id
      : (details.account && details.account.id) != null
        ? details.account.id
        : details.customer_id != null
          ? details.customer_id
          : firstOrder.customer_id != null
            ? firstOrder.customer_id
            : (accountDetails && accountDetails.id) != null
              ? accountDetails.id
              : null;
    const pickupIdVal = firstOrder.id != null ? firstOrder.id : firstOrder.pucode;
    if (accountIdVal == null || pickupIdVal == null) {
      setSignatureRequirements([]);
      setSignaturePickupId(null);
      setSignatureAccountId(null);
      return;
    }
    const accountIdForSignatureFlags = (accountDetails && accountDetails.id != null) ? accountDetails.id : accountIdVal;
    let prescriptions = buildPrescriptionsFromOrders(unprocessedOrders, orderFullDetailsMap);
    const allPrescriptionsEmptyFlags = prescriptions.length > 0 && prescriptions.every((p) => !p.flags || p.flags.length === 0);
    const needFlags = allPrescriptionsEmptyFlags || prescriptions.length === 0;

    const runRequest = function (flagsToUse) {
      let finalPrescriptions = prescriptions;
      if (allPrescriptionsEmptyFlags && flagsToUse.length > 0) {
        finalPrescriptions = prescriptions.map((p) => ({ id: p.id, flags: flagsToUse }));
      } else if (prescriptions.length === 0 && flagsToUse.length > 0) {
        finalPrescriptions = unprocessedOrders.map((order) => ({
          id: String(order.id != null ? order.id : order.pucode || order.id),
          flags: flagsToUse,
        }));
      } else if (prescriptions.length === 0) {
        finalPrescriptions = [{ id: String(pickupIdVal), flags: flagsToUse.length > 0 ? flagsToUse : [] }];
      }
      setSignatureRequirementsLoading(true);
      fetchSignatureRequirements(pickupIdVal, accountIdVal, finalPrescriptions)
        .then((res) => {
          const requirements = res.requirements != null ? res.requirements : [];
          const hasExistingSignature = unprocessedOrders.some(function (order) {
            const fullDetails = orderFullDetailsMap[order.id] || order;
            const sigUrl = (fullDetails && fullDetails.signature_url) || (order && order.signature_url);
            return sigUrl && typeof sigUrl === "string" && sigUrl.trim() !== "" && sigUrl !== "null" && sigUrl !== "undefined";
          });
          if (hasExistingSignature && requirements.length > 0) {
            setSignatureRequirementsFulfilled(true);
            setSignatureSignedFlagLabels(requirements.map(function (r) { return (r.flagLabel || r.displayName || "").trim(); }).filter(Boolean));
            setSignatureRequirements([]);
            signatureRequirementsFulfilledRef.current = true;
          } else if (!signatureRequirementsFulfilledRef.current) {
            setSignatureRequirements(requirements);
          }
          setSignaturePickupId(res.pickupId != null ? res.pickupId : pickupIdVal);
          setSignatureAccountId(accountIdVal);
        })
        .catch(() => {
          setSignatureRequirements([]);
          setSignaturePickupId(String(pickupIdVal));
          setSignatureAccountId(accountIdVal);
        })
        .finally(() => setSignatureRequirementsLoading(false));
    };

    // Fetch account signature flags from API (use T&C account when available so we get AOB etc.)
    fetchAccountSignatureFlagCodes(accountIdForSignatureFlags)
      .then(function (apiCodes) {
        var codes = Array.isArray(apiCodes) ? apiCodes : [];
        setAccountSignatureFlagCodes(codes);
        runRequest(needFlags ? codes : []);
      })
      .catch(function () {
        setAccountSignatureFlagCodes([]);
        runRequest([]);
      });
  }, [open, unprocessedOrders, orderFullDetailsMap, accountDetails]);

  // Get consultation status (check only from scanned orders)
  // Priority: If there's a mix, show pending (not done) state
  const getConsultationStatus = useMemo(() => {
    // If no bags scanned, return null (disabled)
    if (scannedOrderIds.length === 0) {
      return null; // null means disabled
    }
    
    // Check which scanned orders require consultation
    const ordersRequiringConsultation = [];
    const ordersNotRequiringConsultation = [];
    
    for (const orderId of scannedOrderIds) {
      const order = unprocessedOrders.find((o) => o.id === orderId);
      if (!order) continue;
      
      const fullDetails = orderFullDetailsMap[orderId] || order;
      const requiresConsultation = fullDetails && fullDetails.items && Array.isArray(fullDetails.items) &&
        fullDetails.items.some(item => item.is_consultrequire === true);
      
      if (requiresConsultation) {
        ordersRequiringConsultation.push({ orderId, fullDetails });
      } else {
        ordersNotRequiringConsultation.push({ orderId, fullDetails });
      }
    }
    
    // If no orders require consultation, show "done" (all don't require it)
    if (ordersRequiringConsultation.length === 0) {
      return "done";
    }
    
    // If there's a mix (some require, some don't), prioritize pending state
    if (ordersNotRequiringConsultation.length > 0) {
      // Check if all required consultations are done
      const allRequiredDone = ordersRequiringConsultation.every(({ fullDetails }) => {
        const hasUnreleasedConsultationItems =
          fullDetails &&
          fullDetails.items &&
          Array.isArray(fullDetails.items) &&
          fullDetails.items.some(item => item.is_consultrequire === true && !item.is_order_released);
        if (hasUnreleasedConsultationItems) {
          return false;
        }
        if (fullDetails && fullDetails.consultationDetails && Array.isArray(fullDetails.consultationDetails)) {
          if (fullDetails.consultationDetails.length === 0) {
            return false;
          }
          return fullDetails.consultationDetails.some((consultation) => {
            const status = (consultation && consultation.Status) || (consultation && consultation.status);
            return status && String(status).trim().toLowerCase() === "completed";
          });
        }
        return false;
      });
      
      return allRequiredDone ? "done" : "pending";
    }
    
    // All scanned orders require consultation - check if all are done
    const allDone = ordersRequiringConsultation.every(({ fullDetails }) => {
      const hasUnreleasedConsultationItems =
        fullDetails &&
        fullDetails.items &&
        Array.isArray(fullDetails.items) &&
        fullDetails.items.some(item => item.is_consultrequire === true && !item.is_order_released);
      if (hasUnreleasedConsultationItems) {
        return false;
      }
      if (fullDetails && fullDetails.consultationDetails && Array.isArray(fullDetails.consultationDetails)) {
        if (fullDetails.consultationDetails.length === 0) {
          return false;
        }
        return fullDetails.consultationDetails.some((consultation) => {
          const status = (consultation && consultation.Status) || (consultation && consultation.status);
          return status && String(status).trim().toLowerCase() === "completed";
        });
      }
      return false;
    });
    
    return allDone ? "done" : "pending";
  }, [scannedOrderIds, unprocessedOrders, orderFullDetailsMap]);

  // Get ID capture images (from any order)
  const idCaptureImages = useMemo(() => {
    for (const order of unprocessedOrders) {
      const fullDetails = orderFullDetailsMap[order.id] || order;
      if (fullDetails && fullDetails.capturedImages && Array.isArray(fullDetails.capturedImages)) {
        const images = fullDetails.capturedImages
          .map((item) => item.image_url)
          .filter((image) => Boolean(image));
        if (images.length > 0) {
          return images;
        }
      }
    }
    return [];
  }, [unprocessedOrders, orderFullDetailsMap, imageRefreshKey]);

  const hasValidDlrawdata = (idData) => {
    if (!idData || !idData.dlrawdata) return false;
    const dlrawdata = idData.dlrawdata;
    return typeof dlrawdata === "string" && 
           dlrawdata.trim() !== "" && 
           dlrawdata !== "null" && 
           dlrawdata !== "undefined";
  };

  // Check if ID is captured (scanned or images)
  const hasScannedId = useMemo(() => {
    for (const order of unprocessedOrders) {
      const fullDetails = orderFullDetailsMap[order.id] || order;
      if (fullDetails && fullDetails.idData && hasValidDlrawdata(fullDetails.idData)) {
        return true;
      }
    }
    return false;
  }, [unprocessedOrders, orderFullDetailsMap]);

  const hasCapturedId = idCaptureImages.length > 0;
  const hasIdCaptured = hasScannedId || hasCapturedId;

  const hasOrdersRequiringId = useMemo(() => {
    return unprocessedOrders.some((order) => {
      const fullDetails = orderFullDetailsMap[order.id] || order;
      const items = (fullDetails && fullDetails.items) || [];
      return Array.isArray(items) && items.some((item) => item && item.is_controlledsubstances === true);
    });
  }, [unprocessedOrders, orderFullDetailsMap]);

  const idData = useMemo(() => {
    for (const order of unprocessedOrders) {
      const fullDetails = orderFullDetailsMap[order.id] || order;
      if (fullDetails && fullDetails.idData) {
        if (hasValidDlrawdata(fullDetails.idData)) {
          return fullDetails.idData;
        }
      }
    }

    for (const order of unprocessedOrders) {
      const fullDetails = orderFullDetailsMap[order.id] || order;
      if (fullDetails && fullDetails.idData) {
        return fullDetails.idData;
      }
    }

    return null;
  }, [unprocessedOrders, orderFullDetailsMap]);

  // Get signature URL (from any order)
  const signatureUrl = useMemo(() => {
    for (const order of unprocessedOrders) {
      const fullDetails = orderFullDetailsMap[order.id] || order;
      const sigUrl = (fullDetails && fullDetails.signature_url) || (order && order.signature_url);
      if (sigUrl && typeof sigUrl === "string" && sigUrl.trim() !== "" && sigUrl !== "null" && sigUrl !== "undefined") {
        return sigUrl;
      }
    }
    return null;
  }, [unprocessedOrders, orderFullDetailsMap]);

  // Collect all consultation notes with order information
  const existingConsultationNotesList = useMemo(() => {
    const notesList = [];
    const seenNotes = new Set();
    
    const extractNotesFromOrder = (order, orderId) => {
      const fullDetails = orderFullDetailsMap[orderId] || order;
      
      if (fullDetails && fullDetails.consultationDetails && Array.isArray(fullDetails.consultationDetails)) {
        if (fullDetails.consultationDetails.length > 0) {
          const currentPucode = fullDetails.pucode || fullDetails.pickup_code || order.pucode || order.pickup_code || "";
          
          let latestCompletedConsultation = null;
          
          for (let i = 0; i < fullDetails.consultationDetails.length; i++) {
            const consultation = fullDetails.consultationDetails[i];
            const status = (consultation && consultation.Status) || (consultation && consultation.status);
            const statusLower = status ? String(status).trim().toLowerCase() : "";
            const notes = (consultation && consultation.consultation_notes) || "";
            const hasNotes = notes && notes.trim() !== "";
            
            const consultationPucode = 
              (consultation && consultation.pucode) ||
              (consultation && consultation.pickup_code) ||
              (consultation && consultation.PUCode) ||
              (consultation && consultation.PickupCode) ||
              (consultation && consultation.pickupCode) ||
              "";
            
            const matchesPucode = !currentPucode || !consultationPucode || 
              String(consultationPucode).trim().toLowerCase() === String(currentPucode).trim().toLowerCase();
            
            if (status && statusLower === "completed" && hasNotes && matchesPucode) {
              latestCompletedConsultation = consultation;
              break;
            }
          }
          
          if (latestCompletedConsultation) {
            const notes = latestCompletedConsultation.consultation_notes || "";
            
            if (notes && notes.trim() !== "") {
              const notesKey = notes.trim();
              const scanStatus = orderBagScanStatus[orderId];
              const scannedLocation =
                scanStatus && scanStatus.matchedBy !== "stock_code"
                  ? scanStatus.scannedValue
                  : "";
              const bagNumber = scannedLocation || order.storage_location || fullDetails.storage_location || "";
              const pucode = fullDetails.pucode || fullDetails.pickup_code || order.pucode || order.pickup_code || "";
              const uniqueKey = `${notesKey}|${bagNumber}|${pucode}`;
              
              if (!seenNotes.has(uniqueKey)) {
                seenNotes.add(uniqueKey);
                
                let consultationStatus = null;
                if (fullDetails && fullDetails.consultationDetails && Array.isArray(fullDetails.consultationDetails)) {
                  const hasCompleted = fullDetails.consultationDetails.some((consultation) => {
                    const status = (consultation && consultation.Status) || (consultation && consultation.status);
                    return status && String(status).trim().toLowerCase() === "completed";
                  });
                  
                  const hasUnreleasedConsultationItems =
                    fullDetails.items &&
                    Array.isArray(fullDetails.items) &&
                    fullDetails.items.some(item => item.is_consultrequire === true && !item.is_order_released);
                  
                  if (hasCompleted && !hasUnreleasedConsultationItems) {
                    consultationStatus = "done";
                  } else if (hasCompleted || hasUnreleasedConsultationItems) {
                    consultationStatus = "pending";
                  }
                }
                
                notesList.push({
                  orderId,
                  notes: notesKey,
                  bagNumber,
                  pickupCode: pucode,
                  order: order,
                  fullDetails: fullDetails,
                  consultationStatus: consultationStatus,
                });
              }
            }
          }
        }
      }
    };
    
    for (const orderId of scannedOrderIds) {
      const order = unprocessedOrders.find((o) => o.id === orderId);
      if (order) {
        extractNotesFromOrder(order, orderId);
      }
    }
    
    for (const order of unprocessedOrders) {
      if (!scannedOrderIds.includes(order.id)) {
        extractNotesFromOrder(order, order.id);
      }
    }
    
    return notesList;
  }, [scannedOrderIds, unprocessedOrders, orderFullDetailsMap, orderBagScanStatus]);

  const ordersRequiringConsultationWithoutNotes = useMemo(() => {
    const ordersWithoutNotes = [];
    
    for (const order of unprocessedOrders) {
      const fullDetails = orderFullDetailsMap[order.id] || order;
      
      const requiresConsultation = 
        (fullDetails.items && Array.isArray(fullDetails.items) && 
         fullDetails.items.some(item => item.is_consultrequire === true && !item.is_order_released)) ||
        (order.items && Array.isArray(order.items) && 
         order.items.some(item => item.is_consultrequire === true && !item.is_order_released));
      
      if (requiresConsultation) {
        const hasNotesInList = existingConsultationNotesList.some(
          noteItem => noteItem.orderId === order.id
        );

        if (!hasNotesInList) {
          const scanStatus = orderBagScanStatus[order.id];
          const scannedLocation =
            scanStatus && scanStatus.matchedBy !== "stock_code"
              ? scanStatus.scannedValue
              : "";
          const bagNumber = scannedLocation || order.storage_location || fullDetails.storage_location || "";
          
          ordersWithoutNotes.push({
            orderId: order.id,
            bagNumber,
            order: order,
            fullDetails: fullDetails,
            consultationStatus: "pending",
          });
        }
      }
    }
    
    return ordersWithoutNotes;
  }, [unprocessedOrders, orderFullDetailsMap, existingConsultationNotesList, orderBagScanStatus]);

  const declineEligibleOrderIds = useMemo(() => {
    return ordersRequiringConsultationWithoutNotes
      .filter((orderItem) => {
        const scanStatus = orderBagScanStatus[orderItem.orderId];
        return scanStatus && scanStatus.isScanned === true && scanStatus.isVerified === true;
      })
      .map((orderItem) => orderItem.orderId);
  }, [ordersRequiringConsultationWithoutNotes, orderBagScanStatus]);

  const handleDeclineConsultation = async (orderIds) => {
    const ids = Array.isArray(orderIds) ? orderIds.filter(Boolean) : [];
    if (ids.length === 0 || !onDeclineConsultation || isDecliningConsultation) {
      return;
    }

    try {
      await onDeclineConsultation(ids);
      toast.success(
        t("Console.ActionPopup.DeclineConsultationSuccess", {
          defaultValue: "Consultation declined.",
        })
      );
    } catch (error) {
      toast.error(
        (error && error.message) ||
          t("Console.ActionPopup.DeclineConsultationFailed", {
            defaultValue: "Failed to decline consultation.",
          })
      );
    }
  };

  const existingConsultationNotes = useMemo(() => {
    return existingConsultationNotesList.length > 0 ? existingConsultationNotesList[0].notes : null;
  }, [existingConsultationNotesList]);

  const hasConsultationNotes = existingConsultationNotesList.length > 0;

  // Button labels
  const scanIdButtonLabel = hasScannedId
    ? t("Console.ActionPopup.RescanId", { defaultValue: "Re-scan ID" })
    : t("Console.ActionPopup.ScanId", { defaultValue: "Scan ID" });
  const idCaptureButtonLabel = hasCapturedId
    ? t("Console.ActionPopup.RecaptureId", { defaultValue: "Recapture ID" })
    : t("Console.ActionPopup.CaptureId", { defaultValue: "Capture ID" });
  const signatureButtonLabel = signatureUrl
    ? t("Console.ActionPopup.RecaptureSignature", { defaultValue: "Recapture Signature" })
    : t("Console.ActionPopup.CaptureSignature", { defaultValue: "Capture Signature" });
  const consultationButtonLabel = hasConsultationNotes
    ? t("Console.ActionPopup.RecaptureConsultationNotes", { defaultValue: "Re-capture Notes" })
    : t("Console.ActionPopup.CaptureConsultationNotes", { defaultValue: "Capture Notes" });

  // Calculate total amount across all orders
  // Calculate total amount only from scanned and verified orders
  // If mixing paid and unpaid orders, only show unpaid amount
  // Aggregate payment breakdown (paid / due / partial) across scanned + verified
  // orders. Partial payments contribute their remaining balance to "due" and the
  // already-charged portion to "paid" so the cashier sees the real outstanding
  // amount instead of the full order total.
  const paymentBreakdown = useMemo(() => {
    const scannedOrders = unprocessedOrders.filter((order) => {
      const scanStatus = orderBagScanStatus[order.id];
      return scanStatus && scanStatus.isScanned === true && scanStatus.isVerified === true;
    });

    let total = 0;
    let paid = 0;
    let due = 0;
    let anyPartial = false;

    scannedOrders.forEach((order) => {
      const fullDetails = orderFullDetailsMap[order.id] || order;
      const orderPrice = Number(fullDetails.price || order.price || 0);
      const isPayLater =
        fullDetails.is_payroll_deduct === true || order.is_payroll_deduct === true;

      const breakdown = getOrderPaymentBreakdown(fullDetails);
      total += breakdown.total;

      if (orderPrice === 0 || isPayLater || breakdown.isPaid) {
        paid += breakdown.total;
        return;
      }

      paid += breakdown.paid;
      due += breakdown.due;
      if (breakdown.isPartiallyPaid) {
        anyPartial = true;
      }
    });

    return {
      total: Number(total.toFixed(2)),
      paid: Number(paid.toFixed(2)),
      due: Number(due.toFixed(2)),
      isPartiallyPaid: anyPartial && due > 0,
    };
  }, [unprocessedOrders, orderFullDetailsMap, orderBagScanStatus, scannedOrderIds]);

  // Headline amount: outstanding balance when something is still owed, otherwise
  // the full total (so fully-paid pickups still show the paid amount, not $0.00).
  const totalAmount = useMemo(
    () =>
      formatCurrency(
        paymentBreakdown.due > 0 ? paymentBreakdown.due : paymentBreakdown.total
      ),
    [paymentBreakdown]
  );

  // True when any scanned order has is_payroll_deduct set (Pay Later)
  const isPayrollDeduct = useMemo(() => {
    return scannedOrderIds.some((id) => {
      const fullDetails = orderFullDetailsMap[id];
      const order = unprocessedOrders.find((o) => o.id === id);
      return (
        (fullDetails && fullDetails.is_payroll_deduct === true) ||
        (order && order.is_payroll_deduct === true)
      );
    });
  }, [scannedOrderIds, orderFullDetailsMap, unprocessedOrders]);

  const isPayLaterActionAllowed = useMemo(() => {
    if (isPayrollDeduct) return true;
    if (payLaterMode === "all_orders") return true;
    if (payLaterMode !== "select_orders") return false;
    return scannedOrderIds.some((id) => {
      const fullDetails = orderFullDetailsMap[id];
      const order = unprocessedOrders.find((o) => o.id === id);
      return (
        (fullDetails &&
          (fullDetails.allow_pay_later === true || fullDetails.allowPayLater === true)) ||
        (order && (order.allow_pay_later === true || order.allowPayLater === true))
      );
    });
  }, [isPayrollDeduct, payLaterMode, scannedOrderIds, orderFullDetailsMap, unprocessedOrders]);

  // Check if all scanned orders are paid (including Pay Later / payroll deduct)
  const isAllPaid = useMemo(() => {
    if (!isPosEnabled) return true;
    if (scannedOrderIds.length === 0) return false; // No scanned orders means not all paid
    
    return scannedOrderIds.every((orderId) => {
      const order = unprocessedOrders.find((o) => o.id === orderId);
      if (!order) return false;
      
      const fullDetails = orderFullDetailsMap[orderId] || order;
      const orderPrice = Number(fullDetails.price || order.price || 0);
      if (orderPrice === 0) return true;
      
      return (
        fullDetails.is_payroll_deduct === true ||
        order.is_payroll_deduct === true ||
        (fullDetails.amount && fullDetails.amount.isPaid === true) ||
        (fullDetails.is_paid === true) ||
        (order.is_paid === true)
      );
    });
  }, [unprocessedOrders, orderFullDetailsMap, isPosEnabled, scannedOrderIds]);

  // Get fully complete orders for confirmation dialog
  const fullyCompleteOrders = useMemo(() => {
    return unprocessedOrders.filter((order) => {
      const completionStatus = orderCompletionStatus[order.id];
      return completionStatus && completionStatus.isFullyComplete === true;
    });
  }, [unprocessedOrders, orderCompletionStatus]);

  // Check if at least one order is fully complete (for enabling Complete All button)
  const hasAtLeastOneCompleteOrder = fullyCompleteOrders.length > 0;

  // Get incomplete orders (orders that are not fully complete)
  // Enrich with storage_location from full details if available
  const incompleteOrders = useMemo(() => {
    return unprocessedOrders
      .filter((order) => {
        const completionStatus = orderCompletionStatus[order.id];
        return !(completionStatus && completionStatus.isFullyComplete === true);
      })
      .map((order) => {
        // Enrich order with storage_location from full details if not present
        const fullDetails = orderFullDetailsMap[order.id];
        if (fullDetails && !order.storage_location && fullDetails.storage_location) {
          return {
            ...order,
            storage_location: fullDetails.storage_location,
          };
        }
        return order;
      });
  }, [unprocessedOrders, orderCompletionStatus, orderFullDetailsMap]);

  // Check if ALL orders are 100% complete
  const areAllOrdersComplete = useMemo(() => {
    if (unprocessedOrders.length === 0) return false;
    return incompleteOrders.length === 0;
  }, [unprocessedOrders.length, incompleteOrders.length]);


  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setBagScanValues({});
      setEditedItemPrices({});
      setEditingPriceKey(null);
      setBagScanErrors({});
      setSelectedOrderId(null);
      setHighlightedOrderId(null);
      setIsCompleting(false);
      remindingRef.current = false;
      setRemindingOrderId(null);
    }
  }, [open]);

  // Scroll to order when selectedOrderId prop changes (from parent) or when dialog opens with data
  useEffect(() => {
    // Wait for data to load (no skeleton) and dialog to be open
    if (!open || showSkeleton || unprocessedOrders.length === 0) {
      return;
    }

    // Function to attempt scroll and focus
    const attemptScrollAndFocus = (orderId) => {
      const cardRef = orderCardRefs.current[orderId];
      
      if (cardRef && ordersListRef.current) {
        setSelectedOrderId(orderId);
        scrollToOrder(orderId);
        return true;
      }
      return false;
    };

    // Determine which order to scroll to
    const orderIdToScroll = selectedOrderIdProp || (unprocessedOrders.length > 0 ? unprocessedOrders[0].id : null);
    
    if (!orderIdToScroll) {
      return;
    }

    // Try immediately
    if (attemptScrollAndFocus(orderIdToScroll)) {
      return;
    }

    // Wait a bit for DOM to be ready, then retry
    const timer1 = setTimeout(() => {
      if (attemptScrollAndFocus(orderIdToScroll)) {
        return;
      }
      
      // Final retry after longer delay
      const timer2 = setTimeout(() => {
        attemptScrollAndFocus(orderIdToScroll);
      }, 300);

      return () => clearTimeout(timer2);
    }, 150);

    return () => clearTimeout(timer1);
  }, [selectedOrderIdProp, open, showSkeleton, unprocessedOrders]); // React to prop changes and data loading

  // Scroll to specific order card
  const scrollToOrder = (orderId) => {
    const cardRef = orderCardRefs.current[orderId];
    if (cardRef && ordersListRef.current) {
      // Calculate position to center the card
      const container = ordersListRef.current;
      const cardTop = cardRef.offsetTop;
      const cardHeight = cardRef.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollPosition = cardTop - (containerHeight / 2) + (cardHeight / 2);
      
      // Scroll immediately (no smooth behavior for initial load)
      container.scrollTo({
        top: scrollPosition,
        behavior: 'auto'
      });
      
      // Highlight will be set by onFocus handler when input gets focus
      // Focus the bag scan input - try multiple times to ensure it works
      const focusInput = () => {
        const inputRef = bagScanInputRefs.current[orderId];
        if (inputRef) {
          inputRef.focus();
          // Also try clicking to ensure focus
          if (document.activeElement !== inputRef) {
            inputRef.click();
          }
          return true;
        }
        return false;
      };

      // Try focusing immediately
      if (!focusInput()) {
        // Retry after a short delay
        setTimeout(() => {
          if (!focusInput()) {
            // Final retry
            setTimeout(() => {
              focusInput();
            }, 300);
          }
        }, 200);
      }
    }
  };

  // Normalize location value helper
  const normalizeLocationValue = (value) => {
    if (typeof value === "string") {
      return value.trim().toUpperCase();
    }
    if (value === null || value === undefined) {
      return "";
    }
    return String(value).trim().toUpperCase();
  };

  // Handle bag scan for individual order
  const handleBagScan = (orderId, scannedValue) => {
    const normalizedInput = enableTrim
      ? SearchUtils.trimLeadingTrailing(scannedValue, trimChars)
      : (scannedValue === null || scannedValue === undefined ? "" : String(scannedValue).trim());
    // Bedside/Curbside shared shelves identify an order by stock code; Counter uses bag location code.
    const orderSummary = unprocessedOrders.find((o) => o.id === orderId);
    const fullDetails = orderFullDetailsMap[orderId] || orderSummary;
    if (!fullDetails) return;
    const locationTypeId = fullDetails.locationtype_id || (orderSummary && orderSummary.locationtype_id);
    const verifyByStockCode = usesStockCodeVerify(locationTypeId);
    const locationCode = fullDetails.location_code || (orderSummary && orderSummary.location_code) || "";
    const stockCode =
      fullDetails.stock_code ||
      fullDetails.stockCode ||
      (orderSummary && (orderSummary.stock_code || orderSummary.stockCode)) ||
      "";
    const expectedCode = verifyByStockCode ? stockCode : locationCode;
    const matchedBy = verifyByStockCode ? "stock_code" : "location_code";
    
    const normalizedScanned = normalizeLocationValue(normalizedInput);
    const normalizedExpectedCode = normalizeLocationValue(expectedCode);

    if (!normalizedScanned) {
      // Clear scan if empty
      setBagScanValues((prev) => {
        const newValues = { ...prev };
        delete newValues[orderId];
        return newValues;
      });
      setBagScanErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[orderId];
        return newErrors;
      });
      if (onUpdateBagScanStatus) {
        onUpdateBagScanStatus(orderId, {
          isScanned: false,
          isVerified: false,
          scannedValue: "",
          matchedBy: "",
        });
      }
      return;
    }

    const matchesExpectedCode =
      normalizedExpectedCode && normalizedScanned === normalizedExpectedCode;
    // Preserve Counter's existing no-location-code fallback. Shared-shelf flows always require stock code.
    const hasCodeRequirement = verifyByStockCode || normalizedExpectedCode;

    if (matchesExpectedCode) {
      setBagScanValues((prev) => ({ ...prev, [orderId]: normalizedInput }));
      setBagScanErrors((prev) => ({ ...prev, [orderId]: "" }));
      if (onUpdateBagScanStatus) {
        onUpdateBagScanStatus(orderId, {
          isScanned: true,
          isVerified: true,
          scannedValue: normalizedInput,
          matchedBy,
        });
      }
    } else if (hasCodeRequirement) {
      setBagScanValues((prev) => ({ ...prev, [orderId]: normalizedInput }));
      setBagScanErrors((prev) => ({
        ...prev,
        [orderId]: verifyByStockCode
          ? t("Console.MultiPickup.InvalidStockCode", {
              defaultValue: "Stock code does not match this order.",
            })
          : t("Console.MultiPickup.InvalidLocation", {
              defaultValue: "Location does not match",
            }),
      }));
      if (onUpdateBagScanStatus) {
        onUpdateBagScanStatus(orderId, {
          isScanned: true,
          isVerified: false,
          scannedValue: normalizedInput,
          matchedBy,
        });
      }
    } else {
      // No location code - consider valid
      setBagScanValues((prev) => ({ ...prev, [orderId]: normalizedInput }));
      setBagScanErrors((prev) => ({ ...prev, [orderId]: "" }));
      if (onUpdateBagScanStatus) {
        onUpdateBagScanStatus(orderId, {
          isScanned: true,
          isVerified: true,
          scannedValue: normalizedInput,
          matchedBy,
        });
      }
    }
  };


  const isMarketplaceItem = (item) => {
    if (!item) return false;
    if (
      item.is_non_rx === true ||
      item.is_non_rx === "true" ||
      item.is_non_rx === 1 ||
      item.is_non_rx === "1"
    ) {
      return true;
    }
    if (item.non_rx_product_id || item.nonRxProductId) return true;
    const itemNumber = item.item_number || item.itemNumber;
    return itemNumber != null && String(itemNumber).trim() !== "";
  };

  // Get order items
  const getOrderItems = (order) => {
    const fullDetails = orderFullDetailsMap[order.id] || order;
    return (
      fullDetails.items ||
      fullDetails.Items ||
      fullDetails.prescriptionItems ||
      fullDetails.prescription_items ||
      []
    );
  };

  const getItemPriceKey = (orderId, item, index) =>
    `${orderId}-${item.id != null ? item.id : (item.prescription_id != null ? item.prescription_id : (item.prescriptionId != null ? item.prescriptionId : `idx-${index}`))}`;

  const getItemDisplayPrice = (orderId, item, index) => {
    const key = getItemPriceKey(orderId, item, index);
    if (editedItemPrices[key] !== undefined && editedItemPrices[key] !== null && editedItemPrices[key] !== "") {
      const n = parseFloat(String(editedItemPrices[key]).replace(/[^0-9.]/g, ""));
      return Number.isFinite(n) ? n : 0;
    }
    const raw = (item.amount_due != null && item.amount_due !== "") ? item.amount_due : ((item.dueAmount != null && item.dueAmount !== "") ? item.dueAmount : ((item.DueAmount != null && item.DueAmount !== "") ? item.DueAmount : ((item.CoPayAmount != null && item.CoPayAmount !== "") ? item.CoPayAmount : ((item.price != null && item.price !== "") ? item.price : 0))));
    return Number.isFinite(parseFloat(raw)) ? parseFloat(raw) : 0;
  };

  const getOrderPaymentDisplayPrice = (order, item, index, status) => {
    const itemPrice = getItemDisplayPrice(order.id, item, index);
    const fullDetails = orderFullDetailsMap[order.id] || order;
    const breakdown = getOrderPaymentBreakdown(fullDetails);

    if (!(breakdown.isPartiallyPaid && breakdown.due > 0)) {
      return itemPrice;
    }

    return status === "paid" ? breakdown.paid : breakdown.due;
  };

  const handleOpenPriceEdit = (key, currentPrice) => {
    setEditingPriceKey(key);
    const initial = currentPrice === 0 || currentPrice === "0" ? "" : String(currentPrice);
    setEditedItemPrices((prev) => ({ ...prev, [key]: initial }));
  };

  const toValidPriceString = (value) => {
    let s = String(value).replace(/[^0-9.]/g, "");
    const parts = s.split(".");
    if (parts.length > 2) {
      s = parts[0] + "." + parts.slice(1).join("");
    }
    if (parts.length >= 2 && parts[1].length > 2) {
      s = parts[0] + "." + parts[1].slice(0, 2);
    }
    return s;
  };

  const handleChangeItemPrice = (key, value) => {
    const valid = toValidPriceString(value);
    setEditedItemPrices((prev) => ({ ...prev, [key]: valid }));
  };

  const handleSaveItemDueAmount = async (item, priceKey, order) => {
    const raw = editedItemPrices[priceKey];
    const amount = (raw !== undefined && raw !== null && raw !== "")
      ? (parseFloat(String(raw).replace(/[^0-9.]/g, "")) || 0)
      : 0;
    const prescriptionDetailId = item.id != null ? item.id : (item.prescription_id != null ? item.prescription_id : item.prescriptionId);
    if (!prescriptionDetailId) {
      setEditingPriceKey(null);
      return;
    }
    const orderIdForRefresh = order && order.id ? order.id : null;
    try {
      if (onUpdateOrderDueAmount) {
        await onUpdateOrderDueAmount(prescriptionDetailId, amount, orderIdForRefresh);
      }
    } finally {
      setEditingPriceKey(null);
      setEditedItemPrices((prev) => {
        const next = { ...prev };
        delete next[priceKey];
        return next;
      });
    }
  };

  // Handle order card click from collect panel
  const handleOrderCardClick = (order) => {
    if (!order || !order.id) return;
    setSelectedOrderId(order.id);
    // Scroll to the order card and focus on input
    scrollToOrder(order.id);
  };

  // Handle cancel order with confirmation
  const handleCancelOrderClick = (orderId) => {
    setCancelOrderId(orderId);
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancel = () => {
    if (cancelOrderId && onCancelOrder) {
      onCancelOrder(cancelOrderId);
    }
    setIsCancelDialogOpen(false);
    setCancelOrderId(null);
  };

  const handleCloseCancelDialog = () => {
    setIsCancelDialogOpen(false);
    setCancelOrderId(null);
  };

  const isReminding = Boolean(remindingOrderId);

  return (
    <Dialog
      id={hideOnPrint ? 'react-no-print' : undefined}
      fullWidth={true}  // Always full width, margins will create spacing
      fullScreen={false}  // Set to false to allow margins
      maxWidth="xl"
      open={open}
      onClose={isReminding ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          position: "relative",
          overflow: "visible",
          height: "90vh",
          maxHeight: "90vh",
          margin: "32px",
        },
      }}
    >

      {/* Dialog Header */}
      <DialogTitle
        sx={{
          textAlign: "center",
          pb: 2,
          pt: 3,
          px: 3,
          background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
          color: "#ffffff",
          position: "relative",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: "1.5rem",
              letterSpacing: "0.02em",
            }}
          >
            {/* Conditional title: "Multi-Pickup Orders" for multiple orders, "Pickup Order" for single order */}
            {unprocessedOrders.length > 1
              ? t("Console.MultiPickup.Title", {
                  defaultValue: "Multi-Pickup Orders",
                })
              : t("Console.MultiPickup.SingleOrderTitle", {
                  defaultValue: "Pickup Order",
                })}
            {customerName && ` - ${customerName}`}
          </Typography>
          
          {/* Patient Information */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {customerDob && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.8,
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontWeight: 600,
                    }}
                  >
                    {t("Console.ActionPopup.DateOfBirth", { defaultValue: "DOB" })}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                    }}
                  >
                    {customerDob}
                  </Typography>
                </Box>
              )}
              
              {customerPhone && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.8,
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontWeight: 600,
                    }}
                  >
                    {t("Console.ActionPopup.PhoneNumber", { defaultValue: "Phone" })}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                    }}
                  >
                    {customerPhone}
                  </Typography>
                </Box>
              )}
              
              {customerEmail && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.8,
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontWeight: 600,
                    }}
                  >
                    {t("Console.ActionPopup.Email", { defaultValue: "Email" })}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                    }}
                  >
                    {customerEmail}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        
        {/* Close Icon - Positioned on the right */}
        <IconButton
          onClick={onClose}
          disabled={isReminding}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent
        sx={{
          p: 0,
          backgroundColor: "#f8fafc",
          overflowX: "hidden",
          overflowY: "auto",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            minHeight: "100%",
            position: "relative",
            flex: 1,
            width: "100%",
          }}
        >
          {/* Main Content Area */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "visible",
              width: "100%",
            }}
          >
            <Grid 
              container 
              spacing={0}
              direction="row"
              sx={{ 
                height: "100%", 
                flex: 1,
                overflow: "visible",
                minHeight: 0,
                display: "flex",
                flexDirection: "row",
              }}
            >
              {/* Left Column - Order Cards */}
              <Grid 
                item 
                xs={12}
                md={7}
                sx={{ 
                  height: "100%", 
                  overflow: "hidden",
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    p: 3,
                    minHeight: 0,
                  }}
                >
                  {/* Orders List - Scrollable */}
                  <Box
                    ref={ordersListRef}
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      overflowX: "hidden",
                      pr: 1,
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#cbd5e1",
                        borderRadius: "4px",
                        "&:hover": {
                          background: "#94a3b8",
                        },
                      },
                    }}
                  >
                    {showSkeleton ? (
                      // Skeleton Loaders
                      Array.from({ length: 2 }).map((_, index) => (
                        <Card
                          key={`skeleton-${index}`}
                          elevation={0}
                          sx={{
                            mb: 3,
                            borderRadius: 2,
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            {/* Prescription Items Label, Storage Location, and Scan Field Skeleton - All in One Line */}
                            <Box 
                              sx={{ 
                                mb: 3, 
                                display: "flex", 
                                alignItems: "center", 
                                gap: 2, 
                                flexWrap: "wrap",
                                pb: 2,
                                borderBottom: "2px solid #f1f5f9",
                              }}
                            >
                              {/* Prescription Items Label Skeleton */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                                <Skeleton variant="circular" width={32} height={32} />
                                <Skeleton variant="text" width={150} height={24} />
                              </Box>
                              
                              {/* Storage Location Skeleton */}
                              <Skeleton variant="rectangular" width={140} height={60} sx={{ borderRadius: "12px" }} />
                              
                              {/* Scan Field Skeleton */}
                              <Skeleton variant="rectangular" width={240} height={40} sx={{ borderRadius: "10px", flex: "0 1 auto" }} />
                            </Box>

                            {/* Table Skeleton */}
                            <Box sx={{ mb: 3 }}>
                              <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 1, borderRadius: 1 }} />
                              <Skeleton variant="rectangular" width="100%" height={50} sx={{ mb: 1, borderRadius: 1 }} />
                              <Skeleton variant="rectangular" width="100%" height={50} sx={{ borderRadius: 1 }} />
                            </Box>

                            {/* Action Buttons Skeleton */}
                            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                              <Skeleton variant="rectangular" width={120} height={43} sx={{ borderRadius: 2 }} />
                              <Skeleton variant="rectangular" width={120} height={43} sx={{ borderRadius: 2 }} />
                              <Skeleton variant="rectangular" width={120} height={43} sx={{ borderRadius: 2 }} />
                            </Box>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      unprocessedOrders.map((order) => {
                      const orderItems = getOrderItems(order);
                      const scanStatus = orderBagScanStatus[order.id];
                      const isHighlighted = highlightedOrderId === order.id;

                      const isVerified = scanStatus && scanStatus.isVerified === true;
                      const isBagVerified = scanStatus && scanStatus.isScanned === true && scanStatus.isVerified === true;
                      const orderActionsLocked = !isBagVerified || isReminding;
                      const remindInProgress = remindingOrderId === order.id;
                      
                      // Determine payment status for the order (including Pay Later / payroll deduct)
                      const fullDetails = orderFullDetailsMap[order.id] || order;
                      const stockCode = getDisplayCode(
                        fullDetails.stock_code,
                        fullDetails.stockCode,
                        order.stock_code,
                        order.stockCode
                      );
                      const puCode = getDisplayCode(
                        fullDetails.pucode,
                        fullDetails.pickup_code,
                        fullDetails.pickupCode,
                        order.pucode,
                        order.pickup_code,
                        order.pickupCode
                      );
                      const isBedsideOrder = fullDetails.locationtype_id === LOCATION_TYPE.Bedside;
                      const isCurbsideOrder = fullDetails.locationtype_id === LOCATION_TYPE.Curbside;
                      const verifyByStockCode = usesStockCodeVerify(fullDetails.locationtype_id);
                      // Bedside and curbside confirm the handoff by typing this code. Showing it above the field lets staff copy it.
                      const showStockCode = Boolean(stockCode) && !verifyByStockCode;
                      const parkingSpotLabel = (
                        fullDetails.parking_spot_label ||
                        order.parking_spot_label ||
                        ""
                      ).trim();
                      const vehicleSubtitle = [
                        fullDetails.vehicle_color || order.vehicle_color,
                        fullDetails.vehicle_make || order.vehicle_make,
                        fullDetails.vehicle_model || order.vehicle_model,
                      ]
                        .map((part) => (part ? String(part).trim() : ""))
                        .filter(Boolean)
                        .join(" ");
                      const bedsideOrderMessage = isBedsideOrder ? ((fullDetails.order_notes || order.order_notes || "").trim()) : "";
                      const orderPrice = Number(fullDetails.price || order.price || 0);
                      const isOrderPayLater =
                        orderPrice > 0 &&
                        (fullDetails.is_payroll_deduct === true || order.is_payroll_deduct === true);
                      const isOrderPaid =
                        orderPrice === 0 ||
                        isOrderPayLater ||
                        (fullDetails.amount && fullDetails.amount.isPaid === true) ||
                        (fullDetails.is_paid === true) ||
                        (order.is_paid === true);

                      // Signature requirements (AOB etc.) are fetched for one pickup; only show on that order's card
                      const isSignatureRequirementsForThisOrder = (signaturePickupId == null || order.id == null || String(order.id) === String(signaturePickupId));
                      
                      return (
                        <Card
                          key={order.id}
                          ref={(el) => {
                            if (el) {
                              orderCardRefs.current[order.id] = el;
                            }
                          }}
                          elevation={0}
                          sx={{
                            mb: 3,
                            borderRadius: 2,
                            border: highlightedOrderId === order.id
                              ? "3px solid #7c3aed"
                              : "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                            boxShadow: highlightedOrderId === order.id
                              ? "0 4px 16px rgba(124, 58, 237, 0.25)"
                              : "0 2px 8px rgba(0, 0, 0, 0.05)",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            {/* Order identity and stock verification */}
                            <Box 
                              sx={{ 
                                mb: 3, 
                                display: "flex", 
                                flexDirection: "column",
                                gap: 1.5,
                                pb: 2.5,
                                borderBottom: "2px solid #f1f5f9",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 2,
                                  flexWrap: "wrap",
                                  width: "100%",
                                }}
                              >
                                {/* Prescription Items Label */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    flexShrink: 0,
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      bgcolor: "#7c3aed",
                                      width: 32,
                                      height: 32,
                                    }}
                                  >
                                    <MedicationIcon sx={{ fontSize: 20 }} />
                                  </Avatar>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: "1rem",
                                      color: "#1e293b",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Prescription Items
                                  </Typography>
                                </Box>

                                {(showStockCode || puCode || (isCurbsideOrder && parkingSpotLabel)) && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: { xs: 2, sm: 3 },
                                      flexWrap: "wrap",
                                      backgroundColor: "#f8fafc",
                                      border: "1px solid #e2e8f0",
                                      borderRadius: "6px",
                                      px: 1.5,
                                      py: 1,
                                    }}
                                  >
                                    {showStockCode && (
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            display: "block",
                                            color: "#64748b",
                                            fontSize: "0.7rem",
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                            letterSpacing: 0,
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          {t("Console.ActionPopup.StockCode", {
                                            defaultValue: "Stock Code",
                                          })}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "#0f172a",
                                            fontFamily: "monospace",
                                            fontSize: "0.9rem",
                                            fontWeight: 700,
                                            letterSpacing: 0,
                                            overflowWrap: "anywhere",
                                          }}
                                        >
                                          {stockCode}
                                        </Typography>
                                      </Box>
                                    )}

                                    {puCode && (
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            display: "block",
                                            color: "#64748b",
                                            fontSize: "0.7rem",
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                            letterSpacing: 0,
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          {t("Console.ActionPopup.PUCode", {
                                            defaultValue: "PU Code",
                                          })}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "#0f172a",
                                            fontFamily: "monospace",
                                            fontSize: "0.9rem",
                                            fontWeight: 700,
                                            letterSpacing: 0,
                                            overflowWrap: "anywhere",
                                          }}
                                        >
                                          {puCode}
                                        </Typography>
                                      </Box>
                                    )}

                                    {isCurbsideOrder && parkingSpotLabel && (
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            display: "block",
                                            color: "#64748b",
                                            fontSize: "0.7rem",
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                            letterSpacing: 0,
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          {t("Console.Curbside.ParkingSpot", {
                                            defaultValue: "Parking Spot",
                                          })}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "#0f172a",
                                            fontSize: "0.9rem",
                                            fontWeight: 700,
                                            letterSpacing: 0,
                                            overflowWrap: "anywhere",
                                          }}
                                        >
                                          {parkingSpotLabel}
                                        </Typography>
                                        {vehicleSubtitle ? (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              display: "block",
                                              color: "#64748b",
                                              fontSize: "0.75rem",
                                              fontWeight: 600,
                                              lineHeight: 1.2,
                                              mt: 0.25,
                                            }}
                                          >
                                            {vehicleSubtitle}
                                          </Typography>
                                        ) : null}
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </Box>

                              {/* Storage location and scan field */}
                              {order.storage_location && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: { xs: "stretch", sm: "center" },
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: 2,
                                    width: "100%",
                                    minWidth: 0,
                                  }}
                                >
                                  {/* Location */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      minWidth: { sm: 175 },
                                      flexShrink: 0,
                                    }}
                                  >
                                    <LocationOnIcon
                                      sx={{
                                        color: isVerified ? "#16a34a" : "#64748b",
                                        fontSize: "1.25rem",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        component="span"
                                        sx={{
                                          display: "block",
                                          color: "#64748b",
                                          fontSize: "0.7rem",
                                          fontWeight: 700,
                                          letterSpacing: 0,
                                          lineHeight: 1.2,
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        {t("Console.ActionPopup.Location", {
                                          defaultValue: "Location",
                                        })}
                                      </Typography>
                                      <Typography
                                        component="span"
                                        sx={{
                                          display: "block",
                                          color: isVerified ? "#16a34a" : "#0f172a",
                                          fontSize: "1rem",
                                          fontWeight: 700,
                                          letterSpacing: 0,
                                          lineHeight: 1.3,
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {order.storage_location}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Vertical Divider */}
                                  <Box
                                    sx={{
                                      width: "1px",
                                      height: "40px",
                                      backgroundColor: isVerified
                                        ? "rgba(34, 197, 94, 0.3)"
                                        : "#e2e8f0",
                                      flexShrink: 0,
                                    }}
                                  />

                                  {/* Large Prominent Scan Field */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      flex: "1 1 auto",
                                      minWidth: 240,
                                    }}
                                  >
                                    <TextField
                                      size="medium"
                                      fullWidth
                                      inputRef={(el) => {
                                        if (el) {
                                          bagScanInputRefs.current[order.id] = el;
                                        }
                                      }}
                                      value={bagScanValues[order.id] || ""}
                                      onChange={(e) => handleBagScan(order.id, e.target.value)}
                                      onFocus={() => setHighlightedOrderId(order.id)}
                                      onBlur={() => setHighlightedOrderId(null)}
                                      error={!!bagScanErrors[order.id]}
                                      helperText={
                                        bagScanErrors[order.id]
                                          ? bagScanErrors[order.id]
                                          : (scanStatus && scanStatus.isVerified)
                                            ? t(
                                                verifyByStockCode
                                                  ? "Console.ActionPopup.StockCodeMatchedHelper"
                                                  : "Console.ActionPopup.BagScanMatchedHelper",
                                                {
                                                  defaultValue: verifyByStockCode
                                                    ? "Stock code verified."
                                                    : "Location verified.",
                                                }
                                              )
                                            : ""
                                      }
                                      placeholder={t(
                                        verifyByStockCode
                                          ? "Console.ActionPopup.ScanStockCodePlaceholder"
                                          : "Console.ActionPopup.ScanBagPlaceholder",
                                        {
                                          defaultValue: verifyByStockCode
                                            ? "Scan or enter stock code"
                                            : "Scan QR or enter 6-digit location code",
                                        }
                                      )}
                                      autoComplete="off"
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <QrCodeScannerIcon
                                              sx={{
                                                color: bagScanErrors[order.id]
                                                  ? "#fecaca"
                                                  : (scanStatus && scanStatus.isVerified)
                                                    ? "#22c55e"
                                                    : "#7c3aed",
                                                fontSize: "1.5rem",
                                              }}
                                            />
                                          </InputAdornment>
                                        ),
                                        sx: {
                                          height: "48px",
                                          backgroundColor: bagScanErrors[order.id]
                                            ? "rgba(254, 202, 202, 0.1)"
                                            : (scanStatus && scanStatus.isVerified)
                                              ? "rgba(34, 197, 94, 0.1)"
                                              : "rgba(124, 58, 237, 0.05)",
                                          fontSize: "1rem",
                                          fontWeight: 500,
                                          "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: bagScanErrors[order.id]
                                              ? "#f87171"
                                              : (scanStatus && scanStatus.isVerified)
                                                ? "#22c55e"
                                                : "#7c3aed",
                                            borderWidth: 2,
                                          },
                                          "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: bagScanErrors[order.id]
                                              ? "#f87171"
                                              : (scanStatus && scanStatus.isVerified)
                                                ? "#16a34a"
                                                : "#6d28d9",
                                            borderWidth: 2,
                                          },
                                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: bagScanErrors[order.id]
                                              ? "#f87171"
                                              : isHighlighted
                                                ? "#7c3aed"
                                                : "#7c3aed",
                                            borderWidth: 3,
                                          },
                                        },
                                      }}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          fontSize: "1rem",
                                          borderRadius: "12px",
                                          fontWeight: 500,
                                        },
                                        "& .MuiInputBase-input": {
                                          padding: "12px 14px",
                                          fontSize: "1rem",
                                        },
                                      }}
                                      FormHelperTextProps={{
                                        sx: {
                                          mt: 0.5,
                                          mx: 0,
                                          color: bagScanErrors[order.id] ? "#f87171" : (scanStatus && scanStatus.isVerified) ? "#22c55e" : "transparent",
                                          fontSize: "0.75rem",
                                          lineHeight: 1.4,
                                          fontWeight: 500,
                                        },
                                      }}
                                    />
                                  </Box>
                                </Box>
                              )}

                              {/* Scan field only when no storage location is available */}
                              {!order.storage_location && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    minWidth: 0,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      flex: "1 1 auto",
                                      minWidth: 240,
                                    }}
                                  >
                                    <TextField
                                      size="medium"
                                      fullWidth
                                      inputRef={(el) => {
                                        if (el) {
                                          bagScanInputRefs.current[order.id] = el;
                                        }
                                      }}
                                      value={bagScanValues[order.id] || ""}
                                      onChange={(e) => handleBagScan(order.id, e.target.value)}
                                      onFocus={() => setHighlightedOrderId(order.id)}
                                      onBlur={() => setHighlightedOrderId(null)}
                                      error={!!bagScanErrors[order.id]}
                                      helperText={
                                        bagScanErrors[order.id]
                                          ? bagScanErrors[order.id]
                                          : (scanStatus && scanStatus.isVerified)
                                            ? t(
                                                verifyByStockCode
                                                  ? "Console.ActionPopup.StockCodeMatchedHelper"
                                                  : "Console.ActionPopup.BagScanMatchedHelper",
                                                {
                                                  defaultValue: verifyByStockCode
                                                    ? "Stock code verified."
                                                    : "Location verified.",
                                                }
                                              )
                                            : ""
                                      }
                                      placeholder={t(
                                        verifyByStockCode
                                          ? "Console.ActionPopup.ScanStockCodePlaceholder"
                                          : "Console.ActionPopup.ScanBagPlaceholder",
                                        {
                                          defaultValue: verifyByStockCode
                                            ? "Scan or enter stock code"
                                            : "Scan QR or enter 6-digit location code",
                                        }
                                      )}
                                      autoComplete="off"
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <QrCodeScannerIcon
                                              sx={{
                                                color: bagScanErrors[order.id]
                                                  ? "#fecaca"
                                                  : (scanStatus && scanStatus.isVerified)
                                                    ? "#22c55e"
                                                    : "#7c3aed",
                                                fontSize: "1.5rem",
                                              }}
                                            />
                                          </InputAdornment>
                                        ),
                                        sx: {
                                          height: "48px",
                                          backgroundColor: bagScanErrors[order.id]
                                            ? "rgba(254, 202, 202, 0.1)"
                                            : (scanStatus && scanStatus.isVerified)
                                              ? "rgba(34, 197, 94, 0.1)"
                                              : "rgba(124, 58, 237, 0.05)",
                                          fontSize: "1rem",
                                          fontWeight: 500,
                                          "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: bagScanErrors[order.id]
                                              ? "#f87171"
                                              : (scanStatus && scanStatus.isVerified)
                                                ? "#22c55e"
                                                : "#7c3aed",
                                            borderWidth: 2,
                                          },
                                          "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: bagScanErrors[order.id]
                                              ? "#f87171"
                                              : (scanStatus && scanStatus.isVerified)
                                                ? "#16a34a"
                                                : "#6d28d9",
                                            borderWidth: 2,
                                          },
                                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: bagScanErrors[order.id]
                                              ? "#f87171"
                                              : isHighlighted
                                                ? "#7c3aed"
                                                : "#7c3aed",
                                            borderWidth: 3,
                                          },
                                        },
                                      }}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          fontSize: "1rem",
                                          borderRadius: "12px",
                                          fontWeight: 500,
                                        },
                                        "& .MuiInputBase-input": {
                                          padding: "12px 14px",
                                          fontSize: "1rem",
                                        },
                                      }}
                                      FormHelperTextProps={{
                                        sx: {
                                          mt: 0.5,
                                          mx: 0,
                                          color: bagScanErrors[order.id] ? "#f87171" : (scanStatus && scanStatus.isVerified) ? "#22c55e" : "transparent",
                                          fontSize: "0.75rem",
                                          lineHeight: 1.4,
                                          fontWeight: 500,
                                        },
                                      }}
                                    />
                                  </Box>
                                </Box>
                              )}
                            </Box>

                            {bedsideOrderMessage ? (
                              <Box
                                sx={{
                                  mb: 3,
                                  p: 2,
                                  borderRadius: 2,
                                  backgroundColor: "#fff7ed",
                                  border: "1px solid #fed7aa",
                                }}
                              >
                                <Typography
                                  sx={{
                                    mb: 0.75,
                                    fontSize: "0.72rem",
                                    fontWeight: 800,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                    color: "#9a3412",
                                  }}
                                >
                                  {t("Console.ActionPopup.BedsideOrderMessage", {
                                    defaultValue: "Message for delivery person",
                                  })}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.9rem",
                                    fontWeight: 600,
                                    lineHeight: 1.5,
                                    color: "#1f2937",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {bedsideOrderMessage}
                                </Typography>
                              </Box>
                            ) : null}

                            {/* Prescription Items Table */}
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ overflowX: "auto" }}>
                                <Table>
                                  <TableHead>
                                    <TableRow
                                      sx={{
                                        backgroundColor: "#f1f5f9",
                                        "& .MuiTableCell-head": {
                                          color: "#1e293b",
                                          fontWeight: 700,
                                          fontSize: "0.75rem",
                                          textTransform: "uppercase",
                                          letterSpacing: "0.05em",
                                          borderBottom: "2px solid #cbd5e1",
                                        },
                                      }}
                                    >
                                      <TableCell>
                                        {t("Console.ActionPopup.RxNumber")}
                                      </TableCell>
                                      <TableCell>
                                        {t("Console.ActionPopup.DrugName")}
                                      </TableCell>
                                      <TableCell align="center">
                                        {t("Console.ActionPopup.Requirements") || "Requirements"}
                                      </TableCell>
                                      <TableCell align="center">
                                        {t("Console.ActionPopup.Flags") || "Flags"}
                                      </TableCell>
                                      <TableCell align="right">
                                        {t("Console.ActionPopup.Status", { defaultValue: "Status" })}
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {orderItems && orderItems.length > 0 ? (
                                      orderItems.map((item, idx) => {
                                        const marketplaceRow = isMarketplaceItem(item);
                                        return (
                                        <TableRow
                                          key={item.id || idx}
                                          sx={{
                                            backgroundColor: marketplaceRow ? "#f3e8ff" : "transparent",
                                            "& td": {
                                              backgroundColor: marketplaceRow ? "#f3e8ff" : "transparent",
                                            },
                                            "&:hover td": {
                                              backgroundColor: marketplaceRow ? "#e9d5ff" : "#f8fafc",
                                            },
                                            "&:last-child td": {
                                              borderBottom: "none",
                                            },
                                          }}
                                        >
                                          <TableCell
                                            sx={{
                                              color: "#1e293b",
                                              fontSize: "0.875rem",
                                            }}
                                          >
                                            {item.rx_no || item.rxNo || "N/A"}
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              color: "#1e293b",
                                              fontSize: "0.875rem",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {item.drug_name || item.drugName || item.name || "N/A"}
                                          </TableCell>
                                          <TableCell align="center" sx={{ py: 1.5 }}>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: 0.5,
                                                minWidth: 100,
                                              }}
                                            >
                                              {item.is_consultrequire === true && isConsultEnabled && !item.is_order_released && (
                                                <Chip
                                                  label={t("Console.ActionPopup.ConsultRequired", {
                                                    defaultValue: "Consult Required",
                                                  })}
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: "#fef3c7",
                                                    color: "#92400e",
                                                    fontWeight: 600,
                                                    fontSize: "0.7rem",
                                                    height: "20px",
                                                    "& .MuiChip-label": {
                                                      px: 0.75,
                                                      py: 0,
                                                    },
                                                  }}
                                                />
                                              )}
                                              {(() => {
                                                // Check if ID is already captured (shared across all patient orders)
                                                // Use hasIdCaptured which checks all orders, not just this one
                                                // Only show badge if ID is required AND no ID is captured for any order
                                                return item.is_controlledsubstances === true && isDlCaptureEnabled && !hasIdCaptured && (
                                                  <Chip
                                                    label={t("Console.ActionPopup.IdRequired", {
                                                      defaultValue: "ID Required",
                                                    })}
                                                    size="small"
                                                    sx={{
                                                      backgroundColor: "#fef3c7",
                                                      color: "#92400e",
                                                      fontWeight: 600,
                                                      fontSize: "0.7rem",
                                                      height: "20px",
                                                      "& .MuiChip-label": {
                                                        px: 0.75,
                                                        py: 0,
                                                      },
                                                    }}
                                                  />
                                                );
                                              })()}
                                              {(() => {
                                                var rawFlags = item.PrescriptionFlags || item.Flags || item.flags;
                                                var flagList = Array.isArray(rawFlags) ? rawFlags : [];
                                                var itemFlagLabels = flagList.map(function (f) {
                                                  if (typeof f === "string") return f;
                                                  if (f && (f.DisplayName || f.label || f.code || f.id || f.Id)) return String(f.DisplayName || f.label || f.code || f.id || f.Id);
                                                  return null;
                                                }).filter(Boolean);
                                                if (isSignatureRequirementsForThisOrder && signatureRequirementsFulfilled && signatureSignedFlagLabels.length > 0) {
                                                  itemFlagLabels = itemFlagLabels.filter(function (l) {
                                                    return !signatureSignedFlagLabels.some(function (s) { return String(l).toLowerCase() === String(s).toLowerCase() || (l && s && (String(l).indexOf(s) !== -1 || String(s).indexOf(l) !== -1)); });
                                                  });
                                                }
                                                if (itemFlagLabels.length > 0) {
                                                  return (
                                                    <Typography
                                                      component="span"
                                                      sx={{
                                                        fontSize: "0.75rem",
                                                        fontWeight: 500,
                                                        color: "#5b21b6",
                                                      }}
                                                    >
                                                      {itemFlagLabels.join(", ")}
                                                    </Typography>
                                                  );
                                                }
                                                return null;
                                              })()}
                                              {isSignatureRequirementsForThisOrder && !signatureRequirementsFulfilled && signatureRequirements && signatureRequirements.length > 0 && (
                                                <Chip
                                                  label={
                                                    t("Console.ActionPopup.SignatureRequired", { defaultValue: "Signature required" }) +
                                                    " (" + signatureRequirements.map(function (r) { return r.displayName || r.flagLabel; }).join(", ") + ")"
                                                  }
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: "#ede9fe",
                                                    color: "#5b21b6",
                                                    fontWeight: 600,
                                                    fontSize: "0.7rem",
                                                    height: "20px",
                                                    "& .MuiChip-label": {
                                                      px: 0.75,
                                                      py: 0,
                                                    },
                                                  }}
                                                />
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell align="center" sx={{ py: 1.5 }}>
                                            {hasEpicFlags(item) ? (
                                              <Tooltip title="View Epic Flags">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleOpenEpicFlagsDialog(item, order.id)}
                                                  sx={{
                                                    color: "#dc2626",
                                                    "&:hover": {
                                                      backgroundColor: "rgba(220, 38, 38, 0.1)",
                                                    },
                                                    p: 0.5,
                                                  }}
                                                >
                                                  <FlagIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            ) : (
                                              <Typography
                                                sx={{
                                                  color: "#cbd5e1",
                                                  fontSize: "0.75rem",
                                                  fontStyle: "italic",
                                                }}
                                              >
                                                -
                                              </Typography>
                                            )}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-end",
                                                gap: 0.5,
                                              }}
                                            >
                                              {isSignatureRequirementsForThisOrder && signatureRequirementsFulfilled && signatureSignedFlagLabels.length > 0 && (
                                                <Chip
                                                  size="small"
                                                  icon={<CheckCircleIcon sx={{ fontSize: "0.875rem !important" }} />}
                                                  label={t("Console.ActionPopup.FlagsCompleted", { defaultValue: "Flags: Completed" })}
                                                  sx={{
                                                    backgroundColor: "rgba(34, 197, 94, 0.15)",
                                                    color: "#16a34a",
                                                    border: "1.5px solid rgba(34, 197, 94, 0.4)",
                                                    fontWeight: 600,
                                                    fontSize: "0.7rem",
                                                    height: "22px",
                                                    "& .MuiChip-icon": { color: "inherit" },
                                                    "& .MuiChip-label": { px: 0.75, py: 0 },
                                                  }}
                                                />
                                              )}
                                              {isPosEnabled && onUpdateOrderDueAmount && !isOrderPaid ? (
                                                (() => {
                                                  const priceKey = getItemPriceKey(order.id, item, idx);
                                                  const isEditing = editingPriceKey === priceKey;
                                                  const displayPrice = getItemDisplayPrice(order.id, item, idx);
                                                  const pendingDisplayPrice = getOrderPaymentDisplayPrice(order, item, idx, "pending");
                                                  return isEditing ? (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                      <TextField
                                                        size="small"
                                                        type="text"
                                                        inputMode="decimal"
                                                        placeholder="0.00"
                                                        value={editedItemPrices[priceKey] != null ? editedItemPrices[priceKey] : (displayPrice === 0 ? "" : String(displayPrice))}
                                                        onChange={(e) => handleChangeItemPrice(priceKey, e.target.value)}
                                                        sx={{
                                                          width: 90,
                                                          "& .MuiOutlinedInput-root": { fontSize: "0.875rem" },
                                                        }}
                                                        InputProps={{
                                                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                        }}
                                                      />
                                                      <Tooltip title={t("Console.ActionPopup.SaveTooltip", { defaultValue: "Save" })}>
                                                        <IconButton
                                                          size="small"
                                                          onClick={() => handleSaveItemDueAmount(item, priceKey, order)}
                                                          sx={{
                                                            color: "#7c3aed",
                                                            "&:hover": { backgroundColor: "rgba(124, 58, 237, 0.1)" },
                                                          }}
                                                        >
                                                          <SaveIcon fontSize="small" />
                                                        </IconButton>
                                                      </Tooltip>
                                                    </Box>
                                                  ) : (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                      <Chip
                                                        label={`${t("Console.ActionPopup.AmountDue", { defaultValue: "Amount Due" })} $${Number.isFinite(pendingDisplayPrice) ? Number(pendingDisplayPrice).toFixed(2) : "0.00"}`}
                                                        size="small"
                                                        sx={{
                                                          backgroundColor: "rgba(245, 158, 11, 0.15)",
                                                          color: "#d97706",
                                                          border: "1.5px solid rgba(245, 158, 11, 0.4)",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          height: "26px",
                                                          "& .MuiChip-label": { px: 1.25, py: 0 },
                                                        }}
                                                      />
                                                      <Tooltip title={t("Console.ActionPopup.EditTooltip", { defaultValue: "Edit amount" })}>
                                                        <IconButton
                                                          size="small"
                                                          onClick={() => handleOpenPriceEdit(priceKey, displayPrice)}
                                                          sx={{
                                                            color: "#7c3aed",
                                                            "&:hover": { backgroundColor: "rgba(124, 58, 237, 0.1)" },
                                                          }}
                                                        >
                                                          <EditIcon fontSize="small" />
                                                        </IconButton>
                                                      </Tooltip>
                                                    </Box>
                                                  );
                                                })()
                                              ) : isPosEnabled && onUpdateOrderDueAmount && isOrderPaid ? (
                                                (() => {
                                                  const displayPrice = getOrderPaymentDisplayPrice(order, item, idx, "paid");
                                                  return (
                                                    <Chip
                                                      label={`${isOrderPayLater ? "Pay Later" : "Paid"} $${Number.isFinite(displayPrice) ? Number(displayPrice).toFixed(2) : "0.00"}`}
                                                      size="small"
                                                      sx={{
                                                        backgroundColor: "rgba(34, 197, 94, 0.15)",
                                                        color: "#16a34a",
                                                        border: "1.5px solid rgba(34, 197, 94, 0.4)",
                                                        fontWeight: 700,
                                                        fontSize: "0.75rem",
                                                        height: "26px",
                                                        "& .MuiChip-label": { px: 1.25, py: 0 },
                                                      }}
                                                    />
                                                  );
                                                })()
                                              ) : (
                                                <Chip
                                                  label={
                                                    isOrderPayLater
                                                      ? `Pay Later $${Number.isFinite(getOrderPaymentDisplayPrice(order, item, idx, "paid")) ? Number(getOrderPaymentDisplayPrice(order, item, idx, "paid")).toFixed(2) : "0.00"}`
                                                      : isOrderPaid
                                                        ? `Paid $${Number.isFinite(getOrderPaymentDisplayPrice(order, item, idx, "paid")) ? Number(getOrderPaymentDisplayPrice(order, item, idx, "paid")).toFixed(2) : "0.00"}`
                                                        : `${t("Console.ActionPopup.AmountDue", { defaultValue: "Amount Due" })} $${Number.isFinite(getOrderPaymentDisplayPrice(order, item, idx, "pending")) ? Number(getOrderPaymentDisplayPrice(order, item, idx, "pending")).toFixed(2) : "0.00"}`
                                                  }
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: isOrderPaid ? "rgba(34, 197, 94, 0.15)" : "rgba(245, 158, 11, 0.15)",
                                                    color: isOrderPaid ? "#16a34a" : "#d97706",
                                                    border: isOrderPaid ? "1.5px solid rgba(34, 197, 94, 0.4)" : "1.5px solid rgba(245, 158, 11, 0.4)",
                                                    fontWeight: 700,
                                                    fontSize: "0.75rem",
                                                    height: "26px",
                                                    "& .MuiChip-label": { px: 1.25, py: 0 },
                                                  }}
                                                />
                                              )}
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                        );
                                      })
                                    ) : (
                                      <TableRow>
                                        <TableCell
                                          colSpan={5}
                                          align="center"
                                          sx={{
                                            py: 4,
                                            color: "#94a3b8",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          No prescription items
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Box>


                            {/* Individual Order Actions */}
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                flexWrap: "wrap",
                              }}
                            >
                              {orderItems.some((item) => item && item.needs_staff) && !addonConfirmedOrders[order.id] ? (
                                <Button
                                  variant="contained"
                                  onClick={() => confirmAddedItems(order.id)}
                                  disabled={addonConfirmingId === order.id}
                                  sx={{
                                    textTransform: "none",
                                    minHeight: "43px",
                                    borderRadius: 2,
                                    fontWeight: 700,
                                    backgroundColor: "#7c3aed",
                                    "&:hover": { backgroundColor: "#6d28d9" },
                                  }}
                                >
                                  {addonConfirmingId === order.id ? "Confirming..." : "Items are with the order"}
                                </Button>
                              ) : null}
                              {onReprintOrder && (
                                <Button
                                  variant="outlined"
                                  startIcon={<PrintIcon />}
                                  onClick={() => onReprintOrder(order)}
                                  disabled={isReminding}
                                  sx={{
                                    textTransform: "none",
                                    minWidth: "180px",
                                    minHeight: "43px",
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    borderColor: "#7c3aed",
                                    color: "#7c3aed",
                                    borderWidth: 2,
                                    "&:hover": {
                                      borderColor: "#6d28d9",
                                      backgroundColor: "rgba(124, 58, 237, 0.1)",
                                      borderWidth: 2,
                                    },
                                  }}
                                >
                                  {t("Console.ActionPopup.ReprintStockLabel", {
                                    defaultValue: "Reprint Stock Label",
                                  })}
                                </Button>
                              )}
                              {onReminderOrder && (
                                <Button
                                  variant="contained"
                                  startIcon={
                                    remindingOrderId === order.id ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : null
                                  }
                                  onClick={async () => {
                                    if (remindingRef.current || !onReminderOrder) return;
                                    remindingRef.current = true;
                                    setRemindingOrderId(order.id);
                                    try {
                                      await onReminderOrder(order.id);
                                    } finally {
                                      remindingRef.current = false;
                                      setRemindingOrderId(null);
                                    }
                                  }}
                                  disabled={isReminding}
                                  sx={{
                                    textTransform: "none",
                                    minWidth: "120px",
                                    minHeight: "43px",
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    background: "#7c3aed",
                                    color: "#ffffff",
                                    boxShadow: "0 4px 14px 0 rgba(124, 58, 237, 0.3)",
                                    cursor: remindInProgress ? "wait" : isReminding ? "not-allowed" : "pointer",
                                    "&:hover": {
                                      background: "#6d28d9",
                                      boxShadow: "0 6px 20px 0 rgba(124, 58, 237, 0.4)",
                                    },
                                    "&:disabled": {
                                      background: remindInProgress ? "#7c3aed" : "#e2e8f0",
                                      color: remindInProgress ? "#ffffff" : "#94a3b8",
                                      opacity: remindInProgress ? 1 : undefined,
                                    },
                                  }}
                                >
                                  {t("Console.ActionPopup.Remind", {
                                    defaultValue: "Remind",
                                  })}
                                </Button>
                              )}
                              {onReturnOrder && (
                                <Button
                                  variant="outlined"
                                  onClick={() => onReturnOrder(order.id)}
                                  disabled={orderActionsLocked}
                                  sx={{
                                    textTransform: "none",
                                    minWidth: "120px",
                                    minHeight: "43px",
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    borderColor: isBagVerified ? "#f59e0b" : "#e2e8f0",
                                    color: isBagVerified ? "#f59e0b" : "#94a3b8",
                                    borderWidth: 2,
                                    cursor: isBagVerified ? "pointer" : "not-allowed",
                                    "&:hover": {
                                      borderColor: isBagVerified ? "#d97706" : "#e2e8f0",
                                      backgroundColor: isBagVerified ? "rgba(245, 158, 11, 0.1)" : "transparent",
                                      borderWidth: 2,
                                    },
                                    "&:disabled": {
                                      borderColor: "#e2e8f0",
                                      color: "#94a3b8",
                                    },
                                  }}
                                >
                                  {t("Console.ActionPopup.Return", {
                                    defaultValue: "Return",
                                  })}
                                </Button>
                              )}
                              {prescriptionHoldResume && onHoldOrder && order.type_id === DEVICE_TYPE.Ready && order.type_id !== DEVICE_TYPE.Hold && (
                                <Button
                                  variant="outlined"
                                  onClick={() => onHoldOrder(order.id)}
                                  disabled={orderActionsLocked}
                                  sx={{
                                    textTransform: "none",
                                    minWidth: "100px",
                                    minHeight: "43px",
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    borderColor: isBagVerified ? "#b45309" : "#e2e8f0",
                                    color: isBagVerified ? "#b45309" : "#94a3b8",
                                    borderWidth: 2,
                                    cursor: isBagVerified ? "pointer" : "not-allowed",
                                    "&:hover": {
                                      borderColor: isBagVerified ? "#92400e" : "#e2e8f0",
                                      backgroundColor: isBagVerified ? "rgba(180, 83, 9, 0.1)" : "transparent",
                                      borderWidth: 2,
                                    },
                                    "&:disabled": {
                                      borderColor: "#e2e8f0",
                                      color: "#94a3b8",
                                    },
                                  }}
                                >
                                  {t("Console.ActionPopup.Hold", { defaultValue: "Hold" })}
                                </Button>
                              )}
                              {prescriptionHoldResume && onReleaseOrder && order.type_id === DEVICE_TYPE.Hold && (
                                <Button
                                  variant="outlined"
                                  onClick={() => onReleaseOrder(order.id)}
                                  disabled={orderActionsLocked}
                                  sx={{
                                    textTransform: "none",
                                    minWidth: "100px",
                                    minHeight: "43px",
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    borderColor: isBagVerified ? "#0f766e" : "#e2e8f0",
                                    color: isBagVerified ? "#0f766e" : "#94a3b8",
                                    borderWidth: 2,
                                    cursor: isBagVerified ? "pointer" : "not-allowed",
                                    "&:hover": {
                                      borderColor: isBagVerified ? "#0d9488" : "#e2e8f0",
                                      backgroundColor: isBagVerified ? "rgba(15, 118, 110, 0.1)" : "transparent",
                                      borderWidth: 2,
                                    },
                                    "&:disabled": {
                                      borderColor: "#e2e8f0",
                                      color: "#94a3b8",
                                    },
                                  }}
                                >
                                  {t("Console.ActionPopup.Release", { defaultValue: "Release" })}
                                </Button>
                              )}
                              {onCancelOrder && (
                                <Button
                                  variant="outlined"
                                  onClick={() => handleCancelOrderClick(order.id)}
                                  disabled={orderActionsLocked}
                                  sx={{
                                    textTransform: "none",
                                    minWidth: "120px",
                                    minHeight: "43px",
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    borderColor: isBagVerified ? "#ef4444" : "#e2e8f0",
                                    color: isBagVerified ? "#ef4444" : "#94a3b8",
                                    borderWidth: 2,
                                    cursor: isBagVerified ? "pointer" : "not-allowed",
                                    "&:hover": {
                                      borderColor: isBagVerified ? "#dc2626" : "#e2e8f0",
                                      backgroundColor: isBagVerified ? "rgba(239, 68, 68, 0.1)" : "transparent",
                                      borderWidth: 2,
                                    },
                                    "&:disabled": {
                                      borderColor: "#e2e8f0",
                                      color: "#94a3b8",
                                    },
                                  }}
                                >
                                  {t("Console.ActionPopup.Cancel", {
                                    defaultValue: "Cancel",
                                  })}
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Right Column - Control Panel */}
              <Grid 
                item 
                xs={12}
                md={5}
                sx={{ 
                  height: "100%", 
                  overflow: "hidden",
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    overflowY: "auto",
                    maxHeight: "100%",
                    p: 3,
                    backgroundColor: "#f8fafc",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#cbd5e1",
                      borderRadius: "4px",
                      "&:hover": {
                        background: "#94a3b8",
                      },
                    },
                  }}
                >
                  {showSkeleton ? (
                    // Control Panel Skeleton Loaders
                    <Fragment>
                      {/* Payment Summary Skeleton */}
                      <Card
                        elevation={0}
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          background: "#ffffff",
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                            <Skeleton variant="text" width={120} height={28} />
                            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
                          </Box>
                          <Skeleton variant="text" width={80} height={24} />
                          <Skeleton variant="text" width={150} height={32} sx={{ mt: 1 }} />
                        </CardContent>
                      </Card>

                      {/* Consultation Skeleton */}
                      <Card
                        elevation={0}
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          background: "#ffffff",
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Skeleton variant="circular" width={32} height={32} />
                              <Skeleton variant="text" width={120} height={24} />
                            </Box>
                            <Skeleton variant="rectangular" width={150} height={36} sx={{ borderRadius: 1 }} />
                          </Box>
                          <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: 1 }} />
                        </CardContent>
                      </Card>

                      {/* ID Capture Skeleton */}
                      <Card
                        elevation={0}
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          background: "#ffffff",
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Skeleton variant="circular" width={32} height={32} />
                              <Skeleton variant="text" width={100} height={24} />
                            </Box>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                              <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
                              <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
                            </Box>
                          </Box>
                          <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1 }} />
                        </CardContent>
                      </Card>

                      {/* Signature Skeleton */}
                      <Card
                        elevation={0}
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          background: "#ffffff",
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Skeleton variant="circular" width={32} height={32} />
                              <Skeleton variant="text" width={100} height={24} />
                            </Box>
                            <Skeleton variant="rectangular" width={150} height={36} sx={{ borderRadius: 1 }} />
                          </Box>
                          <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 1 }} />
                        </CardContent>
                      </Card>
                    </Fragment>
                  ) : (
                    <Fragment>
                      {/* Payment Summary Card */}
                      {isPosEnabled && (
                    <Card
                      elevation={0}
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        border: "1px solid #e2e8f0",
                        background: "#ffffff",
                      }}
                    >
                      <CardContent>
                        <PaymentSummary
                          t={t}
                          isPaid={isAllPaid}
                          isPayrollDeduct={isPayrollDeduct}
                          totalAmount={totalAmount}
                          isPartiallyPaid={paymentBreakdown.isPartiallyPaid}
                          paidAmount={paymentBreakdown.paid}
                          dueAmount={paymentBreakdown.due}
                          onTogglePaymentStatus={() => {
                            if (onTogglePaymentStatus) {
                              onTogglePaymentStatus(scannedOrderIds);
                            }
                          }}
                          isUpdatingPaymentStatus={isUpdatingPaymentStatus}
                          disableToggle={scannedOrderIds.length === 0 || isReminding}
                          errorMessage={paymentStatusError}
                          onTerminalPayment={() => {
                            if (onTerminalPayment) {
                              onTerminalPayment(scannedOrderIds);
                            }
                          }}
                          isProcessingTerminalPayment={isProcessingTerminalPayment}
                          onPayrollDeduct={
                            isPayLaterActionAllowed
                              ? () => {
                                  if (onPayrollDeduct) {
                                    onPayrollDeduct(scannedOrderIds);
                                  }
                                }
                              : undefined
                          }
                          isProcessingPayLater={isProcessingPayLater}
                          onCashPayment={() => setCashPaymentDialogOpen(true)}
                          isProcessingCashPayment={isProcessingCashPayment}
                          paymentDeviceSelector={paymentDeviceSelector}
                        />
                      </CardContent>
                    </Card>
                  )}

                  {/* Consultation Notes Section - Moved from Consultation Card */}
                  {isConsultEnabled && (
                    <Card
                      elevation={0}
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        border: "1px solid #e2e8f0",
                        background: "#ffffff",
                      }}
                    >
                      <CardContent>
                        {/* Consultation Notes Section with Batch Capture Button */}
                        <Box>
                          {/* Header Section */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 2.5,
                              pb: 2,
                              borderBottom: "2px solid #f1f5f9",
                              flexWrap: "wrap",
                              gap: 2,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  bgcolor: "#7c3aed",
                                  width: 32,
                                  height: 32,
                                }}
                              >
                                <VideoCallIcon sx={{ fontSize: 20 }} />
                              </Avatar>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "1.1rem",
                                  color: "#1e293b",
                                }}
                              >
                                {t("Console.ActionPopup.ConsultationNotes", {
                                  defaultValue: "Consultation Notes",
                                })}
                              </Typography>
                            </Box>
                            
                            {/* Batch Capture Notes / Decline Buttons */}
                            {getConsultationStatus !== null && (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Tooltip
                                  title={
                                    scannedOrderIds.length > 0
                                      ? t("Console.ActionPopup.BatchCaptureNotesTooltip", {
                                          defaultValue: `This will ${hasConsultationNotes ? "update" : "capture"} consultation notes for all scanned and verified orders`,
                                          action: hasConsultationNotes ? "update" : "capture"
                                        })
                                      : t("Console.ActionPopup.BatchCaptureNotesTooltipNoOrders", {
                                          defaultValue: "Scan and verify orders to enable batch capture",
                                        })
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <span>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        if (hasConsultationNotes) {
                                          setConfirmRecaptureNotesOpen(true);
                                        } else {
                                          setConsultationNotesDialogOpen(true);
                                        }
                                      }}
                                      startIcon={<CreateIcon />}
                                      disabled={scannedOrderIds.length === 0 || isReminding}
                                      sx={{
                                        textTransform: "none",
                                        borderWidth: 2,
                                        borderColor: "#7c3aed",
                                        color: "#7c3aed",
                                        fontWeight: 600,
                                        minWidth: 150,
                                        px: 2,
                                        py: 0.75,
                                        borderRadius: 2,
                                        "&:hover": {
                                          borderColor: "#6d28d9",
                                          backgroundColor: "rgba(124, 58, 237, 0.08)",
                                        },
                                        "&.Mui-disabled": {
                                          borderColor: "#e2e8f0",
                                          color: "#cbd5e1",
                                        },
                                      }}
                                    >
                                      {consultationButtonLabel}
                                    </Button>
                                  </span>
                                </Tooltip>
                                {declineEligibleOrderIds.length > 0 && (
                                  <Tooltip
                                    title={t("Console.ActionPopup.DeclineConsultationTooltip", {
                                      defaultValue: "Decline consultation for scanned and verified pending consult orders",
                                    })}
                                    arrow
                                    placement="top"
                                  >
                                    <span>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleDeclineConsultation(declineEligibleOrderIds)}
                                        disabled={isDecliningConsultation || isReminding}
                                        sx={{
                                          textTransform: "none",
                                          borderWidth: 2,
                                          borderColor: "#dc2626",
                                          color: "#dc2626",
                                          fontWeight: 600,
                                          minWidth: 170,
                                          px: 2,
                                          py: 0.75,
                                          borderRadius: 2,
                                          "&:hover": {
                                            borderColor: "#b91c1c",
                                            backgroundColor: "rgba(220, 38, 38, 0.08)",
                                          },
                                          "&.Mui-disabled": {
                                            borderColor: "#e2e8f0",
                                            color: "#cbd5e1",
                                          },
                                        }}
                                      >
                                        {isDecliningConsultation
                                          ? t("Console.ActionPopup.DecliningConsultation", {
                                              defaultValue: "Declining...",
                                            })
                                          : t("Console.ActionPopup.DeclineConsultation", {
                                              defaultValue: "Decline",
                                            })}
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            )}
                          </Box>
                          
                          {/* Display orders requiring consultation but without notes */}
                          {ordersRequiringConsultationWithoutNotes.length > 0 && (
                            ordersRequiringConsultationWithoutNotes.map((orderItem, index) => {
                              const scanStatus = orderBagScanStatus[orderItem.orderId];
                              const isOrderScanned = scanStatus && scanStatus.isScanned === true && scanStatus.isVerified === true;
                              
                              return (
                                <Box
                                  key={`pending-${orderItem.orderId}-${index}`}
                                  sx={{
                                    mb: 2,
                                    p: 2.5,
                                    backgroundColor: "#fffbeb", // Light amber background
                                    borderRadius: 2,
                                    border: "2px solid #f59e0b", // Amber border
                                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.15)",
                                  }}
                                >
                                  {/* Bag Number, Status, and Button in One Line */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                      mb: 1.5,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {/* Bag Number */}
                                    {orderItem.bagNumber && (
                                      <Chip
                                        icon={<LocationOnIcon sx={{ fontSize: "1rem" }} />}
                                        label={
                                          <Box>
                                            <Typography
                                              component="span"
                                              sx={{
                                                fontSize: "0.7rem",
                                                fontWeight: 600,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.05em",
                                                color: "#92400e",
                                                display: "block",
                                                lineHeight: 1.2,
                                              }}
                                            >
                                              {t("Console.ActionPopup.Location", {
                                                defaultValue: "Location",
                                              })}
                                            </Typography>
                                            <Typography
                                              component="span"
                                              sx={{
                                                fontSize: "0.875rem",
                                                fontWeight: 700,
                                                color: "#92400e",
                                                display: "block",
                                                lineHeight: 1.2,
                                              }}
                                            >
                                              {orderItem.bagNumber}
                                            </Typography>
                                          </Box>
                                        }
                                        sx={{
                                          backgroundColor: "rgba(245, 158, 11, 0.15)",
                                          border: "1px solid #f59e0b",
                                          height: "auto",
                                          py: 0.5,
                                          px: 1,
                                          "& .MuiChip-icon": {
                                            color: "#92400e",
                                          },
                                        }}
                                      />
                                    )}
                                    
                                    {/* Consultation Status Chip - Pending */}
                                    <Chip
                                      icon={<ScheduleIcon sx={{ fontSize: "1rem" }} />}
                                      label={
                                        <Box>
                                          <Typography
                                            component="span"
                                            sx={{
                                              fontSize: "0.7rem",
                                              fontWeight: 600,
                                              textTransform: "uppercase",
                                              letterSpacing: "0.05em",
                                              color: "#92400e",
                                              display: "block",
                                              lineHeight: 1.2,
                                            }}
                                          >
                                            {t("Console.ActionPopup.Status", {
                                              defaultValue: "Status",
                                            })}
                                          </Typography>
                                          <Typography
                                            component="span"
                                            sx={{
                                              fontSize: "0.875rem",
                                              fontWeight: 700,
                                              color: "#92400e",
                                              display: "block",
                                              lineHeight: 1.2,
                                            }}
                                          >
                                            {t("Console.ActionPopup.Pending", {
                                              defaultValue: "Pending",
                                            })}
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{
                                        backgroundColor: "rgba(245, 158, 11, 0.15)",
                                        border: "1px solid #f59e0b",
                                        height: "auto",
                                        py: 0.5,
                                        px: 1,
                                        "& .MuiChip-icon": {
                                          color: "#92400e",
                                        },
                                      }}
                                    />
                                    
                                    {/* Capture Notes Button */}
                                    <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={!isOrderScanned || isDecliningConsultation || isReminding}
                                        onClick={() => handleDeclineConsultation([orderItem.orderId])}
                                        sx={{
                                          borderColor: "#dc2626",
                                          color: "#dc2626",
                                          fontWeight: 600,
                                          px: 2,
                                          py: 0.75,
                                          borderRadius: 2,
                                          "&:hover": {
                                            borderColor: "#b91c1c",
                                            backgroundColor: "rgba(220, 38, 38, 0.08)",
                                          },
                                          "&.Mui-disabled": {
                                            borderColor: "#e2e8f0",
                                            color: "#cbd5e1",
                                          },
                                        }}
                                      >
                                        {isDecliningConsultation
                                          ? t("Console.ActionPopup.DecliningConsultation", {
                                              defaultValue: "Declining...",
                                            })
                                          : t("Console.ActionPopup.DeclineConsultation", {
                                              defaultValue: "Decline",
                                            })}
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={!isOrderScanned || isReminding}
                                        onClick={() => {
                                          // First time capture - no confirmation needed
                                          setSelectedOrderIdForRecapture(orderItem.orderId);
                                          setSelectedNoteForRecapture("");
                                          setConsultationNotesDialogOpen(true);
                                        }}
                                        startIcon={<CreateIcon />}
                                        sx={{
                                          borderColor: "#f59e0b",
                                          color: "#92400e",
                                          fontWeight: 600,
                                          px: 2,
                                          py: 0.75,
                                          borderRadius: 2,
                                          "&:hover": {
                                            borderColor: "#d97706",
                                            backgroundColor: "rgba(245, 158, 11, 0.08)",
                                          },
                                          "&.Mui-disabled": {
                                            borderColor: "#e2e8f0",
                                            color: "#cbd5e1",
                                          },
                                        }}
                                      >
                                        {t("Console.ActionPopup.CaptureNotes", {
                                          defaultValue: "Capture Notes",
                                        })}
                                      </Button>
                                    </Box>
                                  </Box>
                                  
                                  {/* Message indicating consultation required */}
                                  <Typography
                                    sx={{
                                      color: "#92400e",
                                      fontSize: "0.875rem",
                                      fontStyle: "italic",
                                      mt: 1,
                                    }}
                                  >
                                    {t("Console.ActionPopup.ConsultationRequiredNoNotes", {
                                      defaultValue: "Consultation required - No notes captured yet",
                                    })}
                                  </Typography>
                                </Box>
                              );
                            })
                          )}
                          
                          {/* Display existing consultation notes with bag number and pickup code */}
                          {hasConsultationNotes && existingConsultationNotesList.length > 0 && (
                            existingConsultationNotesList.map((noteItem, index) => (
                                <Box
                                  key={`${noteItem.orderId}-${index}`}
                                  sx={{
                                    mb: index < existingConsultationNotesList.length - 1 ? 2 : 0,
                                    p: 2.5,
                                    backgroundColor: "#f0fdf4", // Light green background
                                    borderRadius: 2,
                                    border: "2px solid #22c55e", // Green border
                                    boxShadow: "0 2px 8px rgba(34, 197, 94, 0.15)",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      boxShadow: "0 4px 12px rgba(34, 197, 94, 0.25)",
                                      transform: "translateY(-1px)",
                                    },
                                  }}
                                >
                                {/* Bag Number, Status, and Button in One Line */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    mb: 1.5,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {/* Bag Number */}
                                  {noteItem.bagNumber && (
                                    <Chip
                                      icon={<LocationOnIcon sx={{ fontSize: "1rem" }} />}
                                      label={
                                        <Box>
                                          <Typography
                                            component="span"
                                            sx={{
                                              fontSize: "0.7rem",
                                              fontWeight: 600,
                                              textTransform: "uppercase",
                                              letterSpacing: "0.05em",
                                              color: "#166534",
                                              display: "block",
                                              lineHeight: 1.2,
                                            }}
                                          >
                                            {t("Console.ActionPopup.Location", {
                                              defaultValue: "Location",
                                            })}
                                          </Typography>
                                          <Typography
                                            component="span"
                                            sx={{
                                              fontSize: "0.875rem",
                                              fontWeight: 700,
                                              color: "#166534",
                                              display: "block",
                                              lineHeight: 1.2,
                                            }}
                                          >
                                            {noteItem.bagNumber}
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{
                                        backgroundColor: "rgba(34, 197, 94, 0.15)",
                                        border: "1px solid #22c55e",
                                        height: "auto",
                                        py: 0.5,
                                        px: 1,
                                        "& .MuiChip-icon": {
                                          color: "#166534",
                                        },
                                      }}
                                    />
                                  )}
                                  
                                  {/* Consultation Status Chip */}
                                  {noteItem.consultationStatus && (
                                    <Chip
                                      icon={
                                        noteItem.consultationStatus === "done" ? (
                                          <CheckCircleIcon sx={{ fontSize: "1rem" }} />
                                        ) : (
                                          <ScheduleIcon sx={{ fontSize: "1rem" }} />
                                        )
                                      }
                                      label={
                                        <Box>
                                          <Typography
                                            component="span"
                                            sx={{
                                              fontSize: "0.7rem",
                                              fontWeight: 600,
                                              textTransform: "uppercase",
                                              letterSpacing: "0.05em",
                                              color: noteItem.consultationStatus === "done" ? "#166534" : "#92400e",
                                              display: "block",
                                              lineHeight: 1.2,
                                            }}
                                          >
                                            {t("Console.ActionPopup.Status", {
                                              defaultValue: "Status",
                                            })}
                                          </Typography>
                                          <Typography
                                            component="span"
                                            sx={{
                                              fontSize: "0.875rem",
                                              fontWeight: 700,
                                              color: noteItem.consultationStatus === "done" ? "#166534" : "#92400e",
                                              display: "block",
                                              lineHeight: 1.2,
                                            }}
                                          >
                                            {noteItem.consultationStatus === "done"
                                              ? t("Console.ActionPopup.Completed", {
                                                  defaultValue: "Completed",
                                                })
                                              : t("Console.ActionPopup.Pending", {
                                                  defaultValue: "Pending",
                                                })}
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{
                                        backgroundColor:
                                          noteItem.consultationStatus === "done"
                                            ? "rgba(34, 197, 94, 0.15)"
                                            : "rgba(245, 158, 11, 0.15)",
                                        border: `1px solid ${
                                          noteItem.consultationStatus === "done" ? "#22c55e" : "#f59e0b"
                                        }`,
                                        height: "auto",
                                        py: 0.5,
                                        px: 1,
                                        "& .MuiChip-icon": {
                                          color: noteItem.consultationStatus === "done" ? "#166534" : "#92400e",
                                        },
                                      }}
                                    />
                                  )}
                                  
                                  {/* Re-capture Button */}
                                  <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                                    {(() => {
                                      // Check if this specific order is scanned and verified
                                      const scanStatus = orderBagScanStatus[noteItem.orderId];
                                      const isOrderScanned = scanStatus && scanStatus.isScanned === true && scanStatus.isVerified === true;
                                      
                                      return (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          disabled={!isOrderScanned || isReminding}
                                          onClick={() => {
                                            setSelectedOrderIdForRecapture(noteItem.orderId);
                                            setSelectedNoteForRecapture(noteItem.notes);
                                            setIndividualRecaptureConfirmOpen(true);
                                          }}
                                          startIcon={<CreateIcon />}
                                          sx={{
                                            borderColor: "#22c55e",
                                            color: "#166534",
                                            fontWeight: 600,
                                            px: 2,
                                            py: 0.75,
                                            borderRadius: 2,
                                            "&:hover": {
                                              borderColor: "#16a34a",
                                              backgroundColor: "rgba(34, 197, 94, 0.08)",
                                            },
                                            "&.Mui-disabled": {
                                              borderColor: "#e2e8f0",
                                              color: "#cbd5e1",
                                            },
                                          }}
                                        >
                                          {t("Console.ActionPopup.RecaptureNotes", {
                                            defaultValue: "Re-capture",
                                          })}
                                        </Button>
                                      );
                                    })()}
                                  </Box>
                                </Box>
                                
                                {/* Notes Content */}
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    pt: 1.5,
                                    borderTop: "1px solid rgba(34, 197, 94, 0.2)",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: "#1e293b",
                                      fontSize: "0.9rem",
                                      lineHeight: 1.6,
                                      whiteSpace: "pre-wrap", // Preserve line breaks
                                      wordBreak: "break-word",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {noteItem.notes}
                                  </Typography>
                                </Box>
                              </Box>
                            ))
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* ID Capture Card */}
                  {isDlCaptureEnabled && (
                    <Card
                      elevation={0}
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        border: hasOrdersRequiringId && !hasIdCaptured
                          ? "1px solid rgba(245, 158, 11, 0.5)"
                          : "1px solid #e2e8f0",
                        background: hasOrdersRequiringId && !hasIdCaptured
                          ? "rgba(245, 158, 11, 0.05)"
                          : "#ffffff",
                        maxHeight: "600px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                          p: 3,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 2.5,
                            pb: 2,
                            borderBottom: "2px solid #f1f5f9",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: "#7c3aed",
                                width: 32,
                                height: 32,
                              }}
                            >
                              <BadgeIcon sx={{ fontSize: 20 }} />
                            </Avatar>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                fontSize: "1.1rem",
                                color: "#1e293b",
                              }}
                            >
                              {t("Report.Consult.IDCapture") || "ID Capture"}
                            </Typography>
                            {hasOrdersRequiringId && !hasIdCaptured && (
                              <Chip
                                label={t("Console.ActionPopup.Required", { defaultValue: "Required" })}
                                size="small"
                                sx={{
                                  backgroundColor: "rgba(254, 243, 199, 0.9)",
                                  color: "#b45309",
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                  height: "22px",
                                  border: "1px solid rgba(245, 158, 11, 0.4)",
                                  "& .MuiChip-label": { px: 1, py: 0 },
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: "flex", gap: 1.5 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                if (hasScannedId) {
                                  setConfirmRescanOpen(true);
                                } else {
                                  setScanIdDialogOpen(true);
                                }
                              }}
                              disabled={isScanningId || scannedOrderIds.length === 0 || isReminding}
                              sx={{
                                textTransform: "none",
                                borderWidth: 2,
                                borderColor: scannedOrderIds.length > 0 ? "#7c3aed" : "#e2e8f0",
                                color: scannedOrderIds.length > 0 ? "#7c3aed" : "#94a3b8",
                                fontWeight: 600,
                                minWidth: 120,
                                cursor: scannedOrderIds.length > 0 ? "pointer" : "not-allowed",
                                "&:hover": {
                                  borderColor: scannedOrderIds.length > 0 ? "#6d28d9" : "#e2e8f0",
                                  backgroundColor: scannedOrderIds.length > 0 ? "rgba(124, 58, 237, 0.08)" : "transparent",
                                },
                                "&:disabled": {
                                  borderColor: "#e2e8f0",
                                  color: "#94a3b8",
                                },
                              }}
                            >
                              {scanIdButtonLabel}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                if (hasCapturedId) {
                                  setConfirmRecaptureOpen(true);
                                } else {
                                  setIdCaptureModalOpen(true);
                                }
                              }}
                              disabled={scannedOrderIds.length === 0 || isReminding}
                              sx={{
                                textTransform: "none",
                                borderWidth: 2,
                                borderColor: scannedOrderIds.length > 0 ? "#7c3aed" : "#e2e8f0",
                                color: scannedOrderIds.length > 0 ? "#7c3aed" : "#94a3b8",
                                fontWeight: 600,
                                minWidth: 120,
                                cursor: scannedOrderIds.length > 0 ? "pointer" : "not-allowed",
                                "&:hover": {
                                  borderColor: scannedOrderIds.length > 0 ? "#6d28d9" : "#e2e8f0",
                                  backgroundColor: scannedOrderIds.length > 0 ? "rgba(124, 58, 237, 0.08)" : "transparent",
                                },
                                "&:disabled": {
                                  borderColor: "#e2e8f0",
                                  color: "#94a3b8",
                                },
                              }}
                            >
                              {idCaptureButtonLabel}
                            </Button>
                          </Box>
                        </Box>
                        {idData && idData.dlrawdata && (
                          <Box
                            sx={{
                              mb: 2.5,
                              pb: 2.5,
                              borderBottom: "2px solid #f1f5f9",
                              flexShrink: 0,
                            }}
                          >
                            <Grid container spacing={2}>
                              {idData.first_name && (
                                <Grid item xs={4}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#64748b",
                                      fontSize: "0.75rem",
                                      mb: 0.5,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    {t("Console.ActionPopup.FirstName", {
                                      defaultValue: "First Name",
                                    })}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: "#1e293b",
                                      fontWeight: 600,
                                      fontSize: "0.95rem",
                                    }}
                                  >
                                    {idData.first_name}
                                  </Typography>
                                </Grid>
                              )}
                              {idData.last_name && (
                                <Grid item xs={4}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#64748b",
                                      fontSize: "0.75rem",
                                      mb: 0.5,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    {t("Console.ActionPopup.LastName", {
                                      defaultValue: "Last Name",
                                    })}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: "#1e293b",
                                      fontWeight: 600,
                                      fontSize: "0.95rem",
                                    }}
                                  >
                                    {idData.last_name}
                                  </Typography>
                                </Grid>
                              )}
                              {idData.drivers_license_no && (
                                <Grid item xs={4}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#64748b",
                                      fontSize: "0.75rem",
                                      mb: 0.5,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    {t("Console.ActionPopup.DriversLicenseNumber", {
                                      defaultValue: "License Number",
                                    })}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: "#1e293b",
                                      fontWeight: 600,
                                      fontSize: "0.95rem",
                                    }}
                                  >
                                    {idData.drivers_license_no}
                                  </Typography>
                                </Grid>
                              )}
                              {idData.selectedstatecode && (
                                <Grid item xs={4}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#64748b",
                                      fontSize: "0.75rem",
                                      mb: 0.5,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    {t("Console.ActionPopup.State", {
                                      defaultValue: "State",
                                    })}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: "#1e293b",
                                      fontWeight: 600,
                                      fontSize: "0.95rem",
                                    }}
                                  >
                                    {idData.selectedstatecode}
                                  </Typography>
                                </Grid>
                              )}
                              {idData.dob && (
                                <Grid item xs={4}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#64748b",
                                      fontSize: "0.75rem",
                                      mb: 0.5,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    {t("Console.ActionPopup.DateOfBirth", {
                                      defaultValue: "Date of Birth",
                                    })}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: "#1e293b",
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    {idData.dob}
                                  </Typography>
                                </Grid>
                              )}
                              {idData.expdate && (
                                <Grid item xs={4}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#64748b",
                                      fontSize: "0.75rem",
                                      mb: 0.5,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    {t("Console.ActionPopup.ExpirationDate", {
                                      defaultValue: "Expiration Date",
                                    })}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: "#1e293b",
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    {idData.expdate}
                                  </Typography>
                                </Grid>
                              )}
                              {idData.dlrawdata && hasValidDlrawdata(idData) && (
                                <Grid item xs={12}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#64748b",
                                      fontSize: "0.75rem",
                                      mb: 0.5,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    {t("Console.ActionPopup.DLRawData", {
                                      defaultValue: "DL Raw Data",
                                    })}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: "#1e293b",
                                      fontSize: "0.8rem",
                                      fontFamily: "monospace",
                                      wordBreak: "break-all",
                                      backgroundColor: "#f8fafc",
                                      p: 1.5,
                                      borderRadius: 1,
                                      border: "1px solid #e2e8f0",
                                    }}
                                  >
                                    {idData.dlrawdata}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        )}
                        <Box
                          sx={{
                            flex: 1,
                            overflow: "visible",
                            minHeight: 0,
                            mt: idData && hasValidDlrawdata(idData) ? 2 : 0,
                          }}
                        >
                          {idCaptureImages && idCaptureImages.length > 0 ? (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                flexWrap: "wrap",
                                pb: 1,
                              }}
                            >
                              {idCaptureImages.map((imageUrl, idx) => (
                                <Box
                                  key={`${imageUrl}-${imageRefreshKey}-${idx}`}
                                  role="button"
                                  tabIndex={0}
                                  aria-label={t("Report.Consult.ViewIDCapture", {
                                    defaultValue: "View ID capture",
                                  })}
                                  onClick={() => {
                                    setSelectedImage(imageUrl);
                                    setImageModalOpen(true);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      setSelectedImage(imageUrl);
                                      setImageModalOpen(true);
                                    }
                                  }}
                                  sx={{
                                    flex: "1 1 calc(50% - 8px)",
                                    minWidth: "200px",
                                    maxWidth: "300px",
                                    cursor: "pointer",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    border: "2px solid #e2e8f0",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                      borderColor: "#7c3aed",
                                      transform: "scale(1.02)",
                                      boxShadow: "0 4px 12px rgba(124, 58, 237, 0.2)",
                                    },
                                  }}
                                >
                                  <img
                                    src={imageUrl}
                                    alt={`ID capture ${idx + 1}`}
                                    style={{
                                      width: "100%",
                                      height: "auto",
                                      display: "block",
                                    }}
                                  />
                                </Box>
                              ))}
                            </Box>
                          ) : hasOrdersRequiringId && !hasIdCaptured ? (
                            <Box
                              sx={{
                                textAlign: "center",
                                py: 4,
                                backgroundColor: "rgba(245, 158, 11, 0.05)",
                                borderRadius: 2,
                                border: "2px dashed rgba(245, 158, 11, 0.5)",
                                minHeight: "180px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <BadgeIcon
                                sx={{
                                  fontSize: 48,
                                  color: "rgba(245, 158, 11, 0.6)",
                                  mb: 1,
                                }}
                              />
                              <Typography
                                sx={{
                                  color: "#b45309",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  mb: 0.5,
                                }}
                              >
                                {t("Console.ActionPopup.Required", { defaultValue: "Required" })}
                              </Typography>
                              <Typography
                                sx={{
                                  color: "#b45309",
                                  fontSize: "0.875rem",
                                }}
                              >
                              {t("Console.ActionPopup.NoIDImages", {
                                defaultValue: "No ID images captured",
                              })}
                              </Typography>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                textAlign: "center",
                                py: 4,
                                backgroundColor: "#f8fafc",
                                borderRadius: 2,
                                border: "2px dashed #e2e8f0",
                                minHeight: "180px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <BadgeIcon
                                sx={{
                                  fontSize: 48,
                                  color: "#94a3b8",
                                  mb: 1,
                                }}
                              />
                              <Typography
                                sx={{
                                  color: "#64748b",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {t("Console.ActionPopup.NoIDImages", {
                                  defaultValue: "No ID images captured",
                                })}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Signature Card */}
                  <Card
                    elevation={0}
                    onClick={() => {
                      if (scannedOrderIds.length > 0) setSignatureModalOpen(true);
                    }}
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      border: signatureUrl
                        ? "1px solid #e2e8f0"
                        : "1px solid rgba(245, 158, 11, 0.5)",
                      background: signatureUrl ? "#ffffff" : "rgba(245, 158, 11, 0.05)",
                      cursor: scannedOrderIds.length > 0 ? "pointer" : "default",
                      "&:hover": {
                        borderColor: scannedOrderIds.length > 0 ? "#7c3aed" : (signatureUrl ? "#e2e8f0" : "rgba(245, 158, 11, 0.5)"),
                        backgroundColor: scannedOrderIds.length > 0 ? "rgba(124, 58, 237, 0.02)" : (signatureUrl ? "#ffffff" : "rgba(245, 158, 11, 0.05)"),
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 2,
                          mb: 2.5,
                          pb: 2,
                          borderBottom: "2px solid #f1f5f9",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "#7c3aed",
                              width: 32,
                              height: 32,
                            }}
                          >
                            <CreateIcon sx={{ fontSize: 20 }} />
                          </Avatar>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              fontSize: "1.1rem",
                              color: "#1e293b",
                            }}
                          >
                            {t("Console.ActionPopup.Signature", {
                              defaultValue: "Signature",
                            })}
                          </Typography>
                          {!signatureUrl && (
                            <Chip
                              label={t("Console.ActionPopup.Required", { defaultValue: "Required" })}
                              size="small"
                              sx={{
                                backgroundColor: "rgba(254, 243, 199, 0.9)",
                                color: "#b45309",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: "22px",
                                border: "1px solid rgba(245, 158, 11, 0.4)",
                                "& .MuiChip-label": { px: 1, py: 0 },
                              }}
                            />
                          )}
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setSignatureModalOpen(true)}
                          disabled={scannedOrderIds.length === 0 || isReminding}
                          sx={{
                            textTransform: "none",
                            borderWidth: 2,
                            borderColor: scannedOrderIds.length > 0 ? "#7c3aed" : "#e2e8f0",
                            color: scannedOrderIds.length > 0 ? "#7c3aed" : "#94a3b8",
                            fontWeight: 600,
                            minWidth: 150,
                            cursor: scannedOrderIds.length > 0 ? "pointer" : "not-allowed",
                            "&:hover": {
                              borderColor: scannedOrderIds.length > 0 ? "#6d28d9" : "#e2e8f0",
                              backgroundColor: scannedOrderIds.length > 0 ? "rgba(124, 58, 237, 0.08)" : "transparent",
                            },
                            "&:disabled": {
                              borderColor: "#e2e8f0",
                              color: "#94a3b8",
                            },
                          }}
                        >
                          {signatureButtonLabel}
                        </Button>
                      </Box>
                      {signatureUrl ? (
                        <Box
                          sx={{
                            border: "2px solid #e2e8f0",
                            borderRadius: 2,
                            overflow: "hidden",
                            p: 2,
                            backgroundColor: "#f8fafc",
                            textAlign: "center",
                            height: "180px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={signatureUrl}
                            alt="signature"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                              display: "block",
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 4,
                            backgroundColor: "rgba(245, 158, 11, 0.05)",
                            borderRadius: 2,
                            border: "2px dashed rgba(245, 158, 11, 0.5)",
                            height: "180px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CreateIcon
                            sx={{
                              fontSize: 48,
                              color: "rgba(245, 158, 11, 0.6)",
                              mb: 1,
                            }}
                          />
                          <Typography
                            sx={{
                              color: "#b45309",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            {t("Console.ActionPopup.Required", { defaultValue: "Required" })}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#b45309",
                              fontSize: "0.875rem",
                            }}
                          >
                            {t("Console.ActionPopup.NoSignature", {
                              defaultValue: "No signature captured yet",
                            })}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                    </Fragment>
                  )}

                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

      </DialogContent>

      {/* Collect Panel - Positioned outside dialog */}
      {unprocessedOrders && unprocessedOrders.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              right: "-85px",
              top: 0,
              bottom: 0,
              width: "85px",
              height: "auto",
              zIndex: 1000,
              pointerEvents: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              overflowY: "visible",
            }}
          >
            <PatientOrdersCollectPanel
              t={t}
              patientOrders={unprocessedOrders}
              selectedOrderId={selectedOrderId || null}
              onOrderSelect={handleOrderCardClick}
              DEVICE_TYPE={DEVICE_TYPE}
              unprocessedOrdersCount={unprocessedOrders.length}
              orderCompletionStatus={orderCompletionStatus}
            />
          </Box>
        )}

      {/* Dialog Actions */}
      <DialogActions
        sx={{
          p: 2,
          backgroundColor: "#ffffff",
          borderTop: "1px solid #e2e8f0",
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            mr: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 1.5,
            px: 2,
            borderRadius: 1,
            backgroundColor: "#f1f5f9",
            borderLeft: "4px solid #64748b",
          }}
        >
          <InfoOutlinedIcon
            sx={{
              color: "#64748b",
              fontSize: 20,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: "#334155",
              fontSize: "0.875rem",
              lineHeight: 1.5,
              fontWeight: 600,
            }}
          >
            {t("Console.MultiPickup.CompleteVsDispenseNote", {
              defaultValue: "Complete: Mark as picked up only in iLocal. Dispense: Mark as dispensed in pharmacy system.",
            })}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
          <Button
            variant="contained"
            onClick={async () => {
              setPendingBatchCompleteIsDispense(false);
              if (areAllOrdersComplete) {
                if (onBatchComplete) {
                  try {
                    setIsCompleting(true);
                    await onBatchComplete(false);
                    toast.success(
                      t("Console.SuccessMessage.ReceiptEmailed", {
                        defaultValue: "Success, receipt has been emailed",
                      })
                    );
                    setTimeout(() => {
                      if (onClose) {
                        onClose();
                      }
                    }, 500);
                  } catch (error) {
                    setIsCompleting(false);
                  }
                }
              } else {
                setIncompleteOrdersList(incompleteOrders);
                setIsIncompleteOrdersDialogOpen(true);
              }
            }}
            disabled={!hasAtLeastOneCompleteOrder || isCompleting || isReminding}
            startIcon={isCompleting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{
              textTransform: "none",
              backgroundColor: "#10b981",
              color: "#ffffff",
              fontWeight: 600,
              px: 3,
              "&:hover": {
                backgroundColor: "#059669",
              },
              "&:disabled": {
                backgroundColor: "#cbd5e1",
                color: "#94a3b8",
              },
            }}
          >
            {unprocessedOrders.length === 1
              ? t("Console.ActionPopup.Complete", {
                  defaultValue: "Complete",
                })
              : t("Console.MultiPickup.CompleteAll", {
                  defaultValue: "Complete All",
                })}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setPendingBatchCompleteIsDispense(true);
              if (areAllOrdersComplete) {
                if (onBatchComplete) {
                  try {
                    setIsCompleting(true);
                    await onBatchComplete(true);
                    toast.success(
                      t("Console.SuccessMessage.ReceiptEmailed", {
                        defaultValue: "Success, receipt has been emailed",
                      })
                    );
                    setTimeout(() => {
                      if (onClose) {
                        onClose();
                      }
                    }, 500);
                  } catch (error) {
                    setIsCompleting(false);
                  }
                }
              } else {
                setIncompleteOrdersList(incompleteOrders);
                setIsIncompleteOrdersDialogOpen(true);
              }
            }}
            disabled={!hasAtLeastOneCompleteOrder || isCompleting || isReminding}
            startIcon={isCompleting ? <CircularProgress size={16} color="inherit" /> : <MedicationIcon />}
            sx={{
              textTransform: "none",
              backgroundColor: "#3b82f6",
              color: "#ffffff",
              fontWeight: 600,
              px: 3,
              "&:hover": {
                backgroundColor: "#2563eb",
              },
              "&:disabled": {
                backgroundColor: "#cbd5e1",
                color: "#94a3b8",
              },
            }}
          >
            {unprocessedOrders.length === 1
              ? t("Console.MultiPickup.Dispense", {
                  defaultValue: "Dispense",
                })
              : t("Console.MultiPickup.DispenseAll", {
                  defaultValue: "Dispense All",
                })}
          </Button>
        </Box>
      </DialogActions>

      {/* Incomplete Orders Dialog */}
      <IncompleteOrdersDialog
        open={isIncompleteOrdersDialogOpen}
        onClose={() => setIsIncompleteOrdersDialogOpen(false)}
        onProceed={async () => {
          setIsIncompleteOrdersDialogOpen(false);
          if (onBatchComplete) {
            try {
              setIsCompleting(true);
              await onBatchComplete(pendingBatchCompleteIsDispense);
              if (onClose) {
                onClose();
              }
            } catch (error) {
              setIsCompleting(false);
            }
          }
        }}
        incompleteOrders={incompleteOrdersList}
        t={t}
        isCompleting={isCompleting}
      />

      {/* Sub-Dialogs */}
      <IDCaptureModal
        open={isIdCaptureModalOpen}
        onClose={() => setIdCaptureModalOpen(false)}
        onSubmit={async (frontImage, backImage) => {
          if (onSubmitIdCapture) {
            try {
              // Determine which orders to update based on capture type
              let orderIdsToUpdate = [];
              
              // Check if any orders already have captured ID images
              const hasCapturedId = patientOrders.some(order => {
                if (!order || !scannedOrderIds.includes(order.id)) return false;
                const fullDetails = orderFullDetailsMap[order.id] || order;
                const capturedImages = fullDetails.capturedImages || [];
                return Array.isArray(capturedImages) && capturedImages.length > 0 && 
                       capturedImages.some(img => img && img.image_url);
              });
              
              if (hasCapturedId) {
                // Re-capture: Pass all orders where bag is scanned and verified (and already have ID capture)
                orderIdsToUpdate = scannedOrderIds.filter(orderId => {
                  const order = patientOrders.find(o => o && o.id === orderId);
                  if (!order) return false;
                  const fullDetails = orderFullDetailsMap[orderId] || order;
                  const capturedImages = fullDetails.capturedImages || [];
                  return Array.isArray(capturedImages) && capturedImages.length > 0;
                });
              } else {
                // Initial capture: Pass all orders where bag is scanned and verified
                orderIdsToUpdate = scannedOrderIds;
              }

              // Pass order IDs for batch capture (if any), otherwise single order
              if (orderIdsToUpdate.length > 0) {
                await onSubmitIdCapture(frontImage, backImage, orderIdsToUpdate);
              } else {
                await onSubmitIdCapture(frontImage, backImage);
              }
              
              // Force refresh of images by updating refresh key
              setImageRefreshKey(prev => prev + 1);
            } catch (error) {
              // Error is handled by parent component
              throw error;
            }
          }
        }}
        t={t}
      />

      <SignatureCaptureModal
        open={isSignatureModalOpen}
        onClose={() => {
          if (!isSubmittingSignature) {
            setSignatureModalOpen(false);
          }
        }}
        signatureRequirements={signatureRequirements}
        signatureRequirementsLoading={signatureRequirementsLoading}
        signatureRequirementsFulfilled={signatureRequirementsFulfilled}
        onSubmit={async (signatureData) => {
          try {
            setSubmittingSignature(true);
            // Append signature to flag form PDFs when required (iLocalBox / prescription flags)
            if (
              signatureRequirements.length > 0 &&
              signaturePickupId != null &&
              signatureAccountId != null
            ) {
              await submitSignatureFlagsForRequirements(
                signaturePickupId,
                signatureAccountId,
                signatureData,
                signatureRequirements,
                { signerName: customerName || undefined, metadata: { context: "counter" } }
              );
              // Clear requirements so they no longer show at prescription level (completed)
              var signedLabels = (signatureRequirements || []).map(function (r) {
                return (r.flagLabel || r.displayName || "").trim();
              }).filter(Boolean);
              setSignatureRequirements([]);
              setSignatureRequirementsFulfilled(true);
              setSignatureSignedFlagLabels(signedLabels);
              signatureRequirementsFulfilledRef.current = true;
            }
            // Save signature on order(s) (counter order signature_url)
            if (onCaptureSignature) {
              const orderIdsToSave = scannedOrderIds.length > 0 ? scannedOrderIds : unprocessedOrders.map((o) => o.id);
              await onCaptureSignature(signatureData, orderIdsToSave);
            }
            setSignatureModalOpen(false);
          } catch (error) {
            console.error("Error capturing signature:", error);
            throw error;
          } finally {
            setSubmittingSignature(false);
          }
        }}
        submitting={isSubmittingSignature}
        t={t}
      />

      <ConsultationNotesDialog
        open={isConsultationNotesDialogOpen}
        onClose={() => {
          setConsultationNotesDialogOpen(false);
          setSelectedOrderIdForRecapture(null);
          setSelectedNoteForRecapture(null);
        }}
        onSave={async (notes) => {
          if (onSaveConsultation) {
            try {
              // If individual recapture, save only to the specific order
              // Otherwise, save to all scanned orders (batch)
              const orderIdsToSave = selectedOrderIdForRecapture 
                ? [selectedOrderIdForRecapture] 
                : scannedOrderIds;
              
              await onSaveConsultation(notes, orderIdsToSave);
              // Close dialog after successful save
              setConsultationNotesDialogOpen(false);
              setSelectedOrderIdForRecapture(null);
              setSelectedNoteForRecapture(null);
            } catch (error) {
              // Error is handled by parent component and displayed in dialog
              // Don't close dialog on error
            }
          }
        }}
        consultationNotes={
          selectedNoteForRecapture !== null 
            ? selectedNoteForRecapture 
            : (existingConsultationNotes || consultationNotes || "")
        }
        t={t}
        isSaving={isSavingConsultation}
        error={consultationError}
      />

      <ScanIdDialog
        open={isScanIdDialogOpen}
        onClose={() => setScanIdDialogOpen(false)}
        enableTrim={enableTrim}
        trimChars={trimChars}
        onSubmit={async (scannedValue) => {
          if (onScanId) {
            // Pass all orderIds from multipickup dialog (all orders are related)
            const orderIdsToSave = unprocessedOrders.map((o) => o.id);
            
            if (orderIdsToSave.length > 0) {
              try {
                await onScanId(scannedValue, orderIdsToSave);
                // Close dialog on success
                setScanIdDialogOpen(false);
              } catch (error) {
                // Error is handled by parent component
              }
            }
          }
        }}
        t={t}
        isSubmitting={isScanningId}
        error={scanIdError}
      />

      {/* Image Modal for viewing ID images */}
      <Dialog
        open={isImageModalOpen}
        onClose={() => {
          setImageModalOpen(false);
          setSelectedImage(null);
        }}
        maxWidth="md"
        fullWidth
        aria-label={t("Report.Consult.ViewIDCapture", {
          defaultValue: "View ID capture",
        })}
        PaperProps={{
          sx: {
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: 3,
            position: "relative",
            border: "1px solid #e2e8f0",
            boxShadow: "0 20px 45px rgba(15, 23, 42, 0.2)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1,
          }}
        >
          <IconButton
            aria-label={t("Global.Close") || "Close"}
            onClick={() => {
              setImageModalOpen(false);
              setSelectedImage(null);
            }}
            sx={{
              backgroundColor: "#ffffff",
              color: "#7c3aed",
              border: "1px solid rgba(124, 58, 237, 0.3)",
              boxShadow: "0 6px 16px rgba(124, 58, 237, 0.25)",
              "&:hover": {
                backgroundColor: "#f3f0ff",
                color: "#5b21b6",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
          }}
        >
          {selectedImage && (
            <img
              src={selectedImage}
              alt="ID capture"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          )}
        </Box>
      </Dialog>

      <CashPaymentDialog
        open={isCashPaymentDialogOpen}
        amountDue={paymentBreakdown.due}
        isProcessing={isProcessingCashPayment}
        onClose={() => setCashPaymentDialogOpen(false)}
        onRecordCash={(cashReceived) => {
          if (!onCashPayment) return false;
          return onCashPayment(scannedOrderIds, cashReceived);
        }}
        t={t}
      />

      {/* Confirmation Dialogs */}
      <Dialog
        open={isConfirmRecaptureOpen}
        onClose={() => setConfirmRecaptureOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 18px 38px rgba(15, 23, 42, 0.24)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 3,
            py: 2.5,
            background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "1.15rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {t("Console.ActionPopup.RecaptureIdConfirmTitle", {
            defaultValue: "Recapture ID?",
          })}
          <IconButton
            size="small"
            onClick={() => setConfirmRecaptureOpen(false)}
            sx={{
              color: "#ffffff",
              "&:hover": { color: "#e0e7ff" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pt: 3,
            pb: 1,
            backgroundColor: "#f8fafc",
          }}
        >
          <DialogContentText
            sx={{
              color: "#475569",
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            {t("Console.ActionPopup.RecaptureIdConfirmMessage", {
              defaultValue:
                "Recapturing will replace the existing ID images. Do you want to continue?",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: "#f8fafc",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setConfirmRecaptureOpen(false)}
            sx={{
              textTransform: "none",
              borderWidth: 2,
              borderColor: "#cbd5e1",
              color: "#475569",
              fontWeight: 600,
              px: 2.5,
              borderRadius: 2,
              "&:hover": {
                borderColor: "#94a3b8",
                backgroundColor: "rgba(148, 163, 184, 0.08)",
              },
            }}
            variant="outlined"
          >
            {t("Console.ActionPopup.Cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            onClick={() => {
              setConfirmRecaptureOpen(false);
              setIdCaptureModalOpen(true);
            }}
            variant="contained"
            color="primary"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
              boxShadow: "0 8px 18px rgba(124, 58, 237, 0.35)",
              px: 2.75,
              borderRadius: 2,
              "&:hover": {
                background:
                  "linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)",
              },
            }}
          >
            {t("Console.ActionPopup.Confirm", { defaultValue: "Confirm" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isConfirmRescanOpen}
        onClose={() => setConfirmRescanOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 18px 38px rgba(15, 23, 42, 0.24)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 3,
            py: 2.5,
            background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "1.15rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {t("Console.ActionPopup.RescanIdConfirmTitle", {
            defaultValue: "Re-scan ID?",
          })}
          <IconButton
            size="small"
            onClick={() => setConfirmRescanOpen(false)}
            sx={{
              color: "#ffffff",
              "&:hover": { color: "#e0e7ff" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pt: 3,
            pb: 1,
            backgroundColor: "#f8fafc",
          }}
        >
          <DialogContentText
            sx={{
              color: "#475569",
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            {t("Console.ActionPopup.RescanIdConfirmMessage", {
              defaultValue:
                "Re-scanning will replace the existing scanned ID data. Do you want to continue?",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: "#f8fafc",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setConfirmRescanOpen(false)}
            sx={{
              textTransform: "none",
              borderWidth: 2,
              borderColor: "#cbd5e1",
              color: "#475569",
              fontWeight: 600,
              px: 2.5,
              borderRadius: 2,
              "&:hover": {
                borderColor: "#94a3b8",
                backgroundColor: "rgba(148, 163, 184, 0.08)",
              },
            }}
            variant="outlined"
          >
            {t("Console.ActionPopup.RescanIdConfirmCancel", {
              defaultValue: "No",
            })}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setConfirmRescanOpen(false);
              setScanIdDialogOpen(true);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
              boxShadow: "0 8px 18px rgba(124, 58, 237, 0.35)",
              px: 2.75,
              borderRadius: 2,
              "&:hover": {
                background:
                  "linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)",
              },
            }}
          >
            {t("Console.ActionPopup.RescanIdConfirmYes", {
              defaultValue: "Yes, Replace",
            })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Re-capture Notes Confirmation Dialog */}
      <Dialog
        open={isConfirmRecaptureNotesOpen}
        onClose={() => setConfirmRecaptureNotesOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 18px 38px rgba(15, 23, 42, 0.24)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 3,
            py: 2.5,
            background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "1.15rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {t("Console.ActionPopup.RecaptureNotesConfirmTitle", {
            defaultValue: "Edit Consultation Notes?",
          })}
          <IconButton
            size="small"
            onClick={() => setConfirmRecaptureNotesOpen(false)}
            sx={{
              color: "#ffffff",
              "&:hover": { color: "#e0e7ff" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pt: 3,
            pb: 1,
            backgroundColor: "#f8fafc",
          }}
        >
          <DialogContentText
            sx={{
              color: "#475569",
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            {t("Console.ActionPopup.RecaptureNotesConfirmMessage", {
              defaultValue:
                "Editing will replace the existing consultation notes. Do you want to continue?",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: "#f8fafc",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setConfirmRecaptureNotesOpen(false)}
            sx={{
              textTransform: "none",
              borderWidth: 2,
              borderColor: "#cbd5e1",
              color: "#475569",
              fontWeight: 600,
              px: 2.5,
              borderRadius: 2,
              "&:hover": {
                borderColor: "#94a3b8",
                backgroundColor: "rgba(148, 163, 184, 0.08)",
              },
            }}
            variant="outlined"
          >
            {t("Console.ActionPopup.Cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            onClick={() => {
              setConfirmRecaptureNotesOpen(false);
              setConsultationNotesDialogOpen(true);
            }}
            variant="contained"
            color="primary"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
              boxShadow: "0 8px 18px rgba(124, 58, 237, 0.35)",
              px: 2.75,
              borderRadius: 2,
              "&:hover": {
                background:
                  "linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)",
              },
            }}
          >
            {t("Console.ActionPopup.Confirm", { defaultValue: "Confirm" })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Re-capture Notes Confirmation Dialog */}
      <Dialog
        open={isIndividualRecaptureConfirmOpen}
        onClose={() => {
          setIndividualRecaptureConfirmOpen(false);
          setSelectedOrderIdForRecapture(null);
          setSelectedNoteForRecapture(null);
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 18px 38px rgba(15, 23, 42, 0.24)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 3,
            py: 2.5,
            background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "1.15rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {t("Console.ActionPopup.RecaptureNotesConfirmTitle", {
            defaultValue: "Edit Consultation Notes?",
          })}
          <IconButton
            size="small"
            onClick={() => {
              setIndividualRecaptureConfirmOpen(false);
              setSelectedOrderIdForRecapture(null);
              setSelectedNoteForRecapture(null);
            }}
            sx={{
              color: "#ffffff",
              "&:hover": { color: "#e0e7ff" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pt: 3,
            pb: 1,
            backgroundColor: "#f8fafc",
          }}
        >
          <DialogContentText
            sx={{
              color: "#475569",
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            {t("Console.ActionPopup.RecaptureNotesConfirmMessage", {
              defaultValue: "Editing will replace the existing consultation notes for this order. Do you want to continue?",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: "#f8fafc",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => {
              setIndividualRecaptureConfirmOpen(false);
              setSelectedOrderIdForRecapture(null);
              setSelectedNoteForRecapture(null);
            }}
            sx={{
              textTransform: "none",
              borderWidth: 2,
              borderColor: "#cbd5e1",
              color: "#475569",
              fontWeight: 600,
              px: 2.5,
              borderRadius: 2,
              "&:hover": {
                borderColor: "#94a3b8",
                backgroundColor: "rgba(148, 163, 184, 0.08)",
              },
            }}
            variant="outlined"
          >
            {t("Console.ActionPopup.Cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            onClick={() => {
              setIndividualRecaptureConfirmOpen(false);
              setConsultationNotesDialogOpen(true);
            }}
            variant="contained"
            color="primary"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
              boxShadow: "0 8px 18px rgba(124, 58, 237, 0.35)",
              px: 2.75,
              borderRadius: 2,
              "&:hover": {
                background:
                  "linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)",
              },
            }}
          >
            {t("Console.ActionPopup.Confirm", { defaultValue: "Confirm" })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Epic Flags Dialog */}
      <EpicFlagsDialog
        open={epicFlagsDialogOpen}
        onClose={handleCloseEpicFlagsDialog}
        item={selectedItemEpicFlags}
        epicFlagResponse={(selectedItemEpicFlags && selectedItemEpicFlags.epic_flag_response) || null}
        rxNumber={(selectedItemEpicFlags && (selectedItemEpicFlags.rx_no || selectedItemEpicFlags.rxNo)) || null}
        t={t}
        accountDetails={accountDetails}
        accountId={accountDetails && accountDetails.id != null ? accountDetails.id : accountIdForDialogs}
        accountLevelFlags={getFlagObjectsFromApiCodes(accountSignatureFlagCodes)}
        signatureRequirementsFulfilled={signatureRequirementsFulfilled}
        signaturePickupId={signaturePickupId}
        epicFlagsOrderId={selectedOrderIdForEpicFlags}
      />

      {/* Cancel Order Confirmation Dialog */}
      <CancelDialog
        open={isCancelDialogOpen}
        onConfirm={handleConfirmCancel}
        onClose={handleCloseCancelDialog}
        t={t}
      />

    </Dialog>
  );
};

MultiPickupDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  hideOnPrint: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  patientOrders: PropTypes.array,
  customerName: PropTypes.string,
  customerDob: PropTypes.string,
  popupData: PropTypes.object,
  DEVICE_TYPE: PropTypes.object.isRequired,
  orderBagScanStatus: PropTypes.object,
  onUpdateBagScanStatus: PropTypes.func,
  onTogglePaymentStatus: PropTypes.func,
  isUpdatingPaymentStatus: PropTypes.bool,
  paymentStatusError: PropTypes.string,
  onTerminalPayment: PropTypes.func,
  isProcessingTerminalPayment: PropTypes.bool,
  onPayrollDeduct: PropTypes.func,
  isProcessingPayLater: PropTypes.bool,
  onCashPayment: PropTypes.func,
  isProcessingCashPayment: PropTypes.bool,
  onCaptureSignature: PropTypes.func,
  onSubmitIdCapture: PropTypes.func,
  onSaveConsultation: PropTypes.func,
  isSavingConsultation: PropTypes.bool,
  onDeclineConsultation: PropTypes.func,
  isDecliningConsultation: PropTypes.bool,
  consultationError: PropTypes.string,
  onStartConsult: PropTypes.func,
  isStartingConsultation: PropTypes.bool,
  onScanId: PropTypes.func,
  isScanningId: PropTypes.bool,
  scanIdError: PropTypes.string,
  onBatchComplete: PropTypes.func,
  onCancelOrder: PropTypes.func,
  onReminderOrder: PropTypes.func,
  onReprintOrder: PropTypes.func,
  onReturnOrder: PropTypes.func,
  prescriptionHoldResume: PropTypes.bool,
  onHoldOrder: PropTypes.func,
  onReleaseOrder: PropTypes.func,
  onUpdateOrderDueAmount: PropTypes.func,
  orderCompletionStatus: PropTypes.object,
  orderFullDetailsMap: PropTypes.object,
  selectedOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool,
  accountDetails: PropTypes.object,
  enableTrim: PropTypes.bool,
  trimChars: PropTypes.string,
  payLaterMode: PropTypes.string,
  paymentDeviceSelector: PropTypes.node,
};

MultiPickupDialog.defaultProps = {
  patientOrders: [],
  customerName: "",
  customerDob: "",
  customerPhone: "",
  customerEmail: "",
  popupData: null,
  consultationNotes: "",
  orderBagScanStatus: {},
  onUpdateBagScanStatus: null,
  onTogglePaymentStatus: undefined,
  isUpdatingPaymentStatus: false,
  paymentStatusError: "",
  onTerminalPayment: undefined,
  isProcessingTerminalPayment: false,
  onCashPayment: undefined,
  isProcessingCashPayment: false,
  onCaptureSignature: undefined,
  onSubmitIdCapture: undefined,
  onSaveConsultation: undefined,
  isSavingConsultation: false,
  onDeclineConsultation: undefined,
  isDecliningConsultation: false,
  consultationError: "",
  onStartConsult: undefined,
  isStartingConsultation: false,
  onScanId: undefined,
  isScanningId: false,
  scanIdError: "",
  onBatchComplete: undefined,
  onCancelOrder: undefined,
  onReminderOrder: undefined,
  onReprintOrder: undefined,
  onReturnOrder: undefined,
  prescriptionHoldResume: false,
  onHoldOrder: undefined,
  onReleaseOrder: undefined,
  onUpdateOrderDueAmount: undefined,
  orderCompletionStatus: {},
  orderFullDetailsMap: {},
  selectedOrderId: null,
  loading: false,
  accountDetails: null,
  enableTrim: false,
  trimChars: SearchUtils.defaultTrimChars,
  payLaterMode: "disabled",
  paymentDeviceSelector: null,
};

export default MultiPickupDialog;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/dialogs/MultiPickupDialog.jsx